import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { hasPermission } from '../lib/permissions';
import { useIsCraneHire } from '../hooks/useCurrentIndustry';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { formatTime, formatDateTime } from '../utils/dateFormatting';
import { hasDepotTimes, deriveDepotTravel } from '../lib/docketTimes';
import { getApiErrorMessage } from '../utils/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ListViewHeader } from '../components/ui/list-view-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RouteProgressList } from '../components/RouteProgressList';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import useInfiniteList from '../hooks/useInfiniteList';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText,
  Search,
  Eye,
  Trash2,
  Calendar,
  Building2,
  MapPin,
  Truck,
  LinkIcon,
  MoreHorizontal,
  Pencil,
  Camera,
  Upload,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Brain,
  Image as ImageIcon,
  Check,
  X,
  Send,
  FileDown,
  ExternalLink,
  Filter,
  User,
  Clock,
  Wrench,
  ClipboardCheck,
  RotateCcw,
  RefreshCw,
  Plus,
  Package,
  Mountain,
  Mail,
  MailX,
  PenLine,
  History,
  AlertTriangle,
  Receipt,
  Layers,
  StickyNote,
  ListPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { ImageLightbox } from '../components/ui/image-lightbox';
import { format, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES_5 = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

// Compute hours (as a decimal) between two HH:MM strings, subtracting lunch/travel mins.
const computeHoursFromTimes = (startTime, finishTime, lunchMinutes = 0, travelMinutes = 0) => {
  if (!startTime || !finishTime) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const [fh, fm] = finishTime.split(':').map(Number);
  const startMins = sh * 60 + sm;
  const finishMins = fh * 60 + fm;
  const diff = finishMins - startMins - (lunchMinutes || 0) + (travelMinutes || 0);
  if (diff <= 0) return null;
  return Math.round((diff / 60) * 100) / 100; // 2dp
};

const computeDocketDateRange = (dateFilter) => {
  const today = new Date();
  switch (dateFilter) {
    case 'today':
      return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
    case 'yesterday': {
      const y = subDays(today, 1);
      return { from: format(y, 'yyyy-MM-dd'), to: format(y, 'yyyy-MM-dd') };
    }
    case 'last_week': {
      const s = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
      const e = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
      return { from: format(s, 'yyyy-MM-dd'), to: format(e, 'yyyy-MM-dd') };
    }
    case 'this_week': {
      const s = startOfWeek(today, { weekStartsOn: 1 });
      const e = endOfWeek(today, { weekStartsOn: 1 });
      return { from: format(s, 'yyyy-MM-dd'), to: format(e, 'yyyy-MM-dd') };
    }
    case 'all':
    default:
      return { from: null, to: null };
  }
};

const Dockets = () => {
  const { api, user } = useAuth();
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const canViewFinancial = hasPermission(user, 'invoicing.read') || hasPermission(user, 'accounting.read');
  const isCraneHire = useIsCraneHire();
  const { isAiAvailable } = useTier();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set()); // empty = all

  const DOCKET_STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'exported', label: 'Exported' },
  ];

  const toggleStatus = (val) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };
  const [dateFilter, setDateFilter] = useState('today');
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const docketsList = useInfiniteList({
    endpoint: '/dockets/paginated',
    params: {
      status: statusFilter.size > 0 ? [...statusFilter].join(',') : '',
      start_date: dateFrom || '',
      end_date: dateTo || '',
    },
  });
  const dockets = docketsList.items;
  const [selectedDocket, setSelectedDocket] = useState(null);
  const [tenantSettings, setTenantSettings] = useState({
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    openai_api_key: null,
    xero_connected: false,
    myob_connected: false,
    qbo_connected: false,
    connected_accounting_package: null
  });

  // Determine which code column(s) to show on the Invoice tab:
  //  - Xero item mode  → Item Code only
  //  - Xero account mode (default) → Account ID only
  //  - MYOB / QBO → Account ID only
  //  - No provider connected → both (pre-populate before connecting)
  const hasAccounting = tenantSettings.xero_connected || tenantSettings.myob_connected || tenantSettings.qbo_connected;
  const showItemCode = !hasAccounting || (tenantSettings.xero_connected && tenantSettings.xero_line_code_mode === 'item');
  const showAccountId = !hasAccounting || !showItemCode || tenantSettings.myob_connected || tenantSettings.qbo_connected;

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);  // history entry to restore
  const [restoring, setRestoring] = useState(false);
  const editBaselineRef = useRef('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingDocket, setEditingDocket] = useState(null);
  const [photoBlobUrls, setPhotoBlobUrls] = useState([]);
  const [editItems, setEditItems] = useState([]);
  const [fuelLevyRemoved, setFuelLevyRemoved] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [postNoteText, setPostNoteText] = useState('');
  const [postNoteSubmitting, setPostNoteSubmitting] = useState(false);
  const [editPoNumber, setEditPoNumber] = useState('');
  const [savingPo, setSavingPo] = useState(false);
  const [editInvoiceRef, setEditInvoiceRef] = useState('');
  const [savingRef, setSavingRef] = useState(false);
  const [editWorkCompleted, setEditWorkCompleted] = useState('');
  const [editMaterialLoads, setEditMaterialLoads] = useState([]);
  const [editLoadDeliveries, setEditLoadDeliveries] = useState([]); // Trailer Load (P3)
  const [equipment, setEquipment] = useState([]); // for Load chip labels
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [attachments, setAttachments] = useState([]); // for Load chip labels
  const [editReason, setEditReason] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [usedPriceList, setUsedPriceList] = useState(null);
  const [tolls, setTolls] = useState([]);

  // XERO Dialog State
  const [xeroDialogOpen, setXeroDialogOpen] = useState(false);
  const [xeroPushing, setXeroPushing] = useState(false);

  // MYOB Dialog State
  const [myobDialogOpen, setMyobDialogOpen] = useState(false);
  const [myobPushing, setMyobPushing] = useState(false);

  // Accounting export history (per-docket)
  const [exportHistory, setExportHistory] = useState([]);
  const [loadingExportHistory, setLoadingExportHistory] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docketToDelete, setDocketToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDockets, setSelectedDockets] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // EW2-300: Group multiple approved dockets into ONE invoice
  const [groupExportOpen, setGroupExportOpen] = useState(false);
  const [groupExporting, setGroupExporting] = useState(false);

  // Multi-accounting: export-time "choose system" prompt (unlinked customers
  // with 2+ connections of the target provider).
  const [connChoiceOpen, setConnChoiceOpen] = useState(false);
  const [connChoiceConnections, setConnChoiceConnections] = useState([]);
  const [connChoiceSelected, setConnChoiceSelected] = useState('');
  const [connChoiceBusy, setConnChoiceBusy] = useState(false);
  const [pendingPush, setPendingPush] = useState(null);

  // Email Docket State
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDocket, setEmailDocket] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [docketEmailInfo, setDocketEmailInfo] = useState(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [emailStatusDialogOpen, setEmailStatusDialogOpen] = useState(false);
  // Multi-recipient state
  const [emailRecipients, setEmailRecipients] = useState([]); // [{email, name, source, checked}]
  const [emailCustomInput, setEmailCustomInput] = useState('');

  // Request Signature (remote) State
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [sigDocket, setSigDocket] = useState(null);
  const [sigLoading, setSigLoading] = useState(false);
  const [sigSending, setSigSending] = useState(false);
  const [sigMode, setSigMode] = useState('customer'); // 'customer' | 'contact' | 'custom'
  const [sigAddress, setSigAddress] = useState('');
  const [sigContacts, setSigContacts] = useState({ customer_contacts: [], site_contacts: [] });
  const [sigSelectedContact, setSigSelectedContact] = useState('');
  const [sigCustomEmail, setSigCustomEmail] = useState('');

  // Send Invoice To Customer State
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceDocket, setInvoiceDocket] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceRecipients, setInvoiceRecipients] = useState(['']);
  const [invoiceSubject, setInvoiceSubject] = useState('');
  const [invoiceBody, setInvoiceBody] = useState('');

  // OCR State
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrImagePreview, setOcrImagePreview] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchTenantSettings = async () => {
    try {
      const response = await api.get(`/tenants/${user.client_id}`);
      setTenantSettings({
        currency_symbol: response.data.currency_symbol || '$',
        tax_name: response.data.tax_name || 'GST',
        tax_rate: response.data.tax_rate ?? 10,
        date_format: response.data.date_format || 'DD/MM/YYYY',
        time_format: response.data.time_format || '24h',
        openai_api_key: response.data.openai_api_key || null,
        xero_connected: response.data.xero_connected || false,
        myob_connected: response.data.myob_connected || false,
        qbo_connected: response.data.qbo_connected || false,
        connected_accounting_package: response.data.connected_accounting_package || null,
        fuel_levy_enabled: response.data.fuel_levy_enabled || false,
        fuel_levy_percentage: response.data.fuel_levy_percentage ?? 0,
        fuel_levy_basis: response.data.fuel_levy_basis || 'invoice_total',
        fuel_levy_account_id: response.data.fuel_levy_account_id || '',
        skip_docket_approval: response.data.skip_docket_approval || false,
        xero_line_code_mode: response.data.xero_line_code_mode || 'account',
        docket_depot_times_enabled: response.data.docket_depot_times_enabled || false
      });
    } catch (error) {
      console.error('Failed to fetch tenant settings');
    }
  };

  // Sync preset date filter → custom date range pickers
  const handleDateFilterChange = useCallback(async (preset) => {
    setDateFilter(preset);
    if (preset === 'all') {
      try {
        const res = await api.get('/dockets/date-range');
        const { min_date, max_date } = res.data || {};
        setDateFrom(min_date || '');
        setDateTo(max_date || '');
      } catch {
        setDateFrom('');
        setDateTo('');
      }
    } else {
      const range = computeDocketDateRange(preset);
      setDateFrom(range.from || '');
      setDateTo(range.to || '');
    }
  }, [api]);

  // When user manually changes a date picker, detach from presets
  const handleDateFromChange = useCallback((val) => {
    setDateFrom(val);
    setDateFilter('custom');
  }, []);

  const handleDateToChange = useCallback((val) => {
    setDateTo(val);
    setDateFilter('custom');
  }, []);

  const fetchDockets = async () => {
    docketsList.reload();
    try {
      // Refresh the open drawer / selected docket with fresh single-record data
      if (editingDocket) {
        const r = await api.get(`/dockets/${editingDocket.id}`);
        if (r.data) {
          setEditingDocket(r.data);
          setEditItems(r.data.items?.map(item => ({ ...item })) || []);
          setEditNotes(r.data.notes || '');
        }
      }
      if (selectedDocket) {
        const r = await api.get(`/dockets/${selectedDocket.id}`);
        if (r.data) setSelectedDocket(r.data);
      }
    } catch (error) {
      // drawer refresh is best-effort
    }
  };

  useEffect(() => {
    // Note: useInfiniteList auto-fetches on mount via its internal useEffect,
    // so we don't call fetchDockets() here to avoid a duplicate reload race.
    setLoading(false);
    fetchTenantSettings();
    api.get('/tolls').then(res => setTolls(res.data || [])).catch(() => {});
    // Trailer Load (P3) — resolve cargo ids to human labels on the Load chips
    (async () => {
      try {
        const [eq, att] = await Promise.all([
          api.get('/equipment'),
          api.get('/attachments'),
        ]);
        setEquipment(eq.data || []);
        setAttachments(att.data || []);
      } catch {
        /* non-critical */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteDocket = async (docketId) => {
    const docket = dockets.find(d => d.id === docketId);
    setDocketToDelete(docket);
    setDeleteDialogOpen(true);
  };

  // Combo line-up for the dockets list: uses the `combo_members` the list
  // endpoint resolves from the job's allocated combo asset.
  const getDocketComboLabels = (docket) => {
    return (docket.combo_members || [])
      .map(m => m.asset_id || m.name || m.component_type_name)
      .filter(Boolean);
  };

  const handleConfirmDelete = async () => {
    if (!docketToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/dockets/${docketToDelete.id}`);
      toast.success('Docket deleted');
      fetchDockets();
      setDeleteDialogOpen(false);
      setDocketToDelete(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete docket'));
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleSelectDocket = (id) => {
    setSelectedDockets(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const isDocketExported = (d) => !!d && (d.status === 'exported' || d.xero_invoice_id || d.myob_invoice_id || d.qbo_invoice_id);

  const handleBulkDelete = async () => {
    if (selectedDockets.length === 0) return;
    setIsBulkDeleting(true);
    try {
      const res = await api.post('/dockets/bulk-delete', { docket_ids: selectedDockets });
      toast.success(`${res.data.deleted_count} docket(s) deleted`);
      setBulkDeleteOpen(false);
      setSelectedDockets([]);
      fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete dockets'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // EW2-300: derived eligibility for grouping selected dockets into one invoice
  const getGroupExportInfo = () => {
    const objs = dockets.filter(d => selectedDockets.includes(d.id));
    const customerIds = [...new Set(objs.map(d => d.customer_id))];
    const allApproved = objs.length > 0 && objs.every(d => d.status === 'approved' && !isDocketExported(d));
    const sameCustomer = customerIds.length === 1 && !!customerIds[0];
    const eligible = objs.length >= 2 && allApproved && sameCustomer;
    const total = objs.reduce((sum, d) => sum + calculateTotal(d.items || []), 0);
    const platforms = [
      tenantSettings.xero_connected ? { key: 'xero', label: 'Xero', path: 'xero' } : null,
      tenantSettings.myob_connected ? { key: 'myob', label: 'MYOB', path: 'myob' } : null,
      tenantSettings.qbo_connected ? { key: 'quickbooks', label: 'QuickBooks', path: 'quickbooks' } : null,
    ].filter(Boolean);
    return { objs, customerIds, allApproved, sameCustomer, eligible, total, platforms };
  };

  // EW2-300: build the exact grouped line-up preview (mirrors the backend invoice composition)
  const buildGroupPreview = (objs) => {
    const sections = objs.map(d => {
      const jn = d.job_number;
      const dn = d.docket_number || (d.id ? d.id.slice(0, 8) : '');
      const date = d.job_date || '';
      const header = `—— ${jn != null ? `Job #${jn} · ` : ''}Docket #${dn}${date ? ` (${date})` : ''} ——`;
      const items = (d.items || []).map(item => {
        const qty = parseFloat(item.qty) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        let desc = item.description || item.type_name || 'Item';
        if (item.uom) desc += ` (${item.uom})`;
        return { desc, qty, unitPrice, amount: qty * unitPrice };
      });
      return { id: d.id, header, items };
    });
    const subtotal = sections.reduce((s, sec) => s + sec.items.reduce((a, it) => a + it.amount, 0), 0);
    const tax = calculateTax(subtotal);
    return { sections, subtotal, tax, total: subtotal + tax };
  };

  const handleGroupExport = async (platform) => {
    setGroupExporting(true);
    try {
      const info = getGroupExportInfo();
      const customerId = info.customerIds[0];
      const r = await resolveTargetConnection({
        provider: platform.key, customerId, single: false,
        docketIds: selectedDockets, platformPath: platform.path, platformLabel: platform.label,
      });
      if (r.choice || r.blocked) return;
      const body = { docket_ids: selectedDockets };
      if (r.connectionId) body.connection_id = r.connectionId;
      const res = await api.post(`/${platform.path}/group-push-invoice`, body);
      toast.success(res.data.message || `Grouped invoice created in ${platform.label}`);
      setGroupExportOpen(false);
      setSelectedDockets([]);
      fetchDockets();
    } catch (error) {
      showExportFailureToast(error, platform.label);
    } finally {
      setGroupExporting(false);
    }
  };

  // Edit Docket Handlers
  const handleOpenEditDialog = (docket) => {
    // Always open the tabbed drawer (read-only for non-draft statuses)
    setEditingDocket(docket);
    setEditItems(docket.items?.map(item => ({ ...item })) || []);
    setFuelLevyRemoved(!!docket.suppress_fuel_levy);
    setEditNotes(docket.notes || '');
    setEditPoNumber(docket.po_number || '');
    setEditInvoiceRef(docket.invoice_reference || '');
    setEditWorkCompleted(docket.work_completed || '');
    setEditMaterialLoads(docket.material_loads?.map(l => ({ ...l })) || []);
    setEditLoadDeliveries(docket.load_deliveries?.map(l => ({ ...l })) || []);
    setEditReason('');
    setUsedPriceList(null); // Reset price list indicator
    setExportHistory([]);
    setIsEditing(docket.status === 'draft'); // Draft dockets open directly in edit mode
    editBaselineRef.current = serializeEditable(
      docket.items?.map(i => ({ ...i })) || [],
      docket.notes || '',
      docket.work_completed || '',
      docket.material_loads?.map(l => ({ ...l })) || [],
      !!docket.suppress_fuel_levy
    );
    setEditDialogOpen(true);
    // Kick off export history fetch (non-blocking)
    fetchDocketExportHistory(docket.id);

    // Load docket email status for the "emailed to X recipients" badge
    setDocketEmailInfo(null);
    api.get(`/dockets/${docket.id}/email-status`)
      .then(res => setDocketEmailInfo(res.data))
      .catch(() => setDocketEmailInfo(null));

    // Load docket photos as authenticated blob URLs (stored SAS tokens expire after 24h)
    setPhotoBlobUrls([]);
    if (docket.photos?.length > 0) {
      docket.photos.forEach((_, idx) => {
        api.get(`/dockets/${docket.id}/photos/${idx}`, { responseType: 'blob' })
          .then(res => {
            const blobUrl = URL.createObjectURL(res.data);
            setPhotoBlobUrls(prev => { const next = [...prev]; next[idx] = blobUrl; return next; });
          })
          .catch(() => {
            setPhotoBlobUrls(prev => { const next = [...prev]; next[idx] = null; return next; });
          });
      });
    }
  };

  // Override the PO number on a docket (allowed until it's exported)
  const handleSavePoNumber = async () => {
    if (!editingDocket) return;
    setSavingPo(true);
    try {
      const res = await api.patch(`/dockets/${editingDocket.id}/po-number`, {
        po_number: editPoNumber.trim() || null,
      });
      const saved = res.data.po_number || '';
      setEditingDocket({ ...editingDocket, po_number: saved });
      setEditPoNumber(saved);
      toast.success('PO number updated');
      fetchDockets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update PO number');
    } finally {
      setSavingPo(false);
    }
  };

  // Override the invoice reference on a docket (allowed until exported to accounting)
  const handleSaveInvoiceRef = async () => {
    if (!editingDocket) return;
    setSavingRef(true);
    try {
      const res = await api.patch(`/dockets/${editingDocket.id}/invoice-reference`, {
        invoice_reference: editInvoiceRef.trim() || null,
      });
      const saved = res.data.invoice_reference || '';
      setEditingDocket({ ...editingDocket, invoice_reference: saved });
      setEditInvoiceRef(saved);
      toast.success('Invoice reference updated');
      fetchDockets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update invoice reference');
    } finally {
      setSavingRef(false);
    }
  };


  // Fetch accounting-export history for a specific docket
  const fetchDocketExportHistory = async (docketId) => {
    if (!docketId) return;
    setLoadingExportHistory(true);
    try {
      const response = await api.get(`/dockets/${docketId}/export-logs`);
      setExportHistory(response.data.logs || []);
    } catch (e) {
      // Silent — history panel simply stays empty
      setExportHistory([]);
    } finally {
      setLoadingExportHistory(false);
    }
  };

  // Show a rich failure toast for invoice export errors.
  // Supports both legacy string details and the new structured payload
  // { error, platform, summary, detail, suggested_fix, technical, status_code }.
  const showExportFailureToast = (error, platformLabel) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.summary) {
      toast.error(detail.summary, {
        description: [
          detail.detail,
          detail.suggested_fix ? `Fix: ${detail.suggested_fix}` : null,
        ].filter(Boolean).join(' · '),
        duration: 12000,
      });
      return;
    }
    const msg = typeof detail === 'string' ? detail : `Failed to push to ${platformLabel}`;
    toast.error(msg);
  };

  const handleEditItemChange = (itemId, field, value) => {
    setEditItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const patch = { [field]: value };
      // Manually editing the price flags the line as overridden so the value
      // survives invoice regeneration (it won't be reset from the price list).
      if (field === 'unit_price') patch.price_overridden = true;
      // EW2-318: manual description edits survive rule regeneration.
      if (field === 'description') patch.description_overridden = true;
      return { ...item, ...patch };
    }));
  };

  const handleAddLineItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      item_type: 'equipment',
      type_name: '',
      display_name: '',
      qty: 0,
      uom: 'HOUR',
      unit_price: 0,
      description: '',
      account_item_id: '',
      invoice_added: true
    };
    setEditItems(prev => [...prev, newItem]);
  };

  // Open the customer's price list catalog picker
  const handleOpenCatalog = async () => {
    if (!editingDocket) return;
    const customerId = editingDocket.customer_id;
    try {
      // Get customer to find their price_list_id
      const customerRes = await api.get(`/customers/${customerId}`);
      const priceListId = customerRes.data?.price_list_id;
      if (!priceListId) {
        toast.info('No price list assigned to this customer');
        return;
      }
      const plRes = await api.get(`/price-lists/${priceListId}`);
      const items = (plRes.data?.items || []).filter(i => i.item_code || i.description);
      if (items.length === 0) {
        toast.info('No catalog items found in the customer\'s price list');
        return;
      }
      setCatalogItems(items);
      setCatalogOpen(true);
    } catch {
      toast.error('Failed to load customer price list');
    }
  };

  // Add a catalog item as a docket line item
  const handleAddCatalogItem = (catalogItem) => {
    const newItem = {
      id: `catalog-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      item_type: 'equipment',
      type_name: catalogItem.description || catalogItem.equipment_type || '',
      display_name: catalogItem.description || catalogItem.equipment_type || '',
      qty: 0,
      uom: catalogItem.uom || 'HOUR',
      unit_price: catalogItem.rate || catalogItem.price || 0,
      description: catalogItem.description || '',
      account_item_id: catalogItem.account_item_id || '',
      item_code: catalogItem.item_code || '',
      invoice_added: true,
    };
    setEditItems(prev => [...prev, newItem]);
    toast.success(`Added: ${catalogItem.item_code || catalogItem.description || 'Item'}`);
  };



  const handleRemoveLineItem = (itemId) => {
    setEditItems(prev => {
      const removed = prev.find(item => item.id === itemId);
      if (removed?.item_type === 'fuel_levy') setFuelLevyRemoved(true);
      return prev.filter(item => item.id !== itemId);
    });
  };

  const handleAddToll = (toll) => {
    const newItem = {
      id: `new-toll-${Date.now()}`,
      item_type: 'toll',
      type_name: toll.name,
      display_name: toll.name,
      description: toll.name,
      qty: 1,
      uom: 'EACH',
      unit_price: toll.price || 0,
      account_item_id: toll.account_id || '',
      toll_id: toll.id,
    };
    setEditItems(prev => [...prev, newItem]);
    toast.success(`Added toll: ${toll.name}`);
  };

  // --- Unsaved-changes guard for the edit drawer ---
  // Serialize only the user-editable fields (fuel_levy line excluded because it
  // auto-recomputes and would create false "dirty" state).
  const serializeEditable = (items, notes, work, loads, fuelRemoved) =>
    JSON.stringify({
      items: (items || [])
        .filter(i => i.item_type !== 'fuel_levy')
        .map(i => ({
          id: i.id, item_type: i.item_type, type_name: i.type_name || '',
          description: i.description || '', qty: Number(i.qty) || 0,
          uom: i.uom || '', unit_price: Number(i.unit_price) || 0,
          account_item_id: i.account_item_id || '',
        })),
      notes: notes || '',
      work: work || '',
      loads: (loads || []).map(l => ({ ...l })),
      fuelRemoved: !!fuelRemoved,
    });

  const isEditDirty = () =>
    isEditing &&
    serializeEditable(editItems, editNotes, editWorkCompleted, editMaterialLoads, fuelLevyRemoved) !== editBaselineRef.current;

  const requestCloseEdit = () => {
    if (isEditDirty()) setDiscardConfirmOpen(true);
    else setEditDialogOpen(false);
  };

  const persistEdits = async () => {
    if (!editingDocket) return false;
    await api.put(`/dockets/${editingDocket.id}`, {
      items: editItems,
      material_loads: editMaterialLoads,
      load_deliveries: (editLoadDeliveries || []).filter(d => d.item_id),
      notes: editNotes,
      work_completed: editWorkCompleted,
      edit_reason: editReason || null,
      suppress_fuel_levy: fuelLevyRemoved,
      version: editingDocket.version
    });
    // Sync the dirty baseline so closing right after a save doesn't prompt.
    editBaselineRef.current = serializeEditable(editItems, editNotes, editWorkCompleted, editMaterialLoads, fuelLevyRemoved);
    return true;
  };

  const handleSaveEdit = async () => {
    if (!editingDocket) return;
    setSaving(true);
    try {
      await persistEdits();
      toast.success('Docket updated & invoice regenerated');
      setEditDialogOpen(false);
      setEditReason('');
      setIsEditing(false);
      fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update docket'));
    } finally {
      setSaving(false);
    }
  };

  // Add a post-submission note
  const handleAddPostNote = async () => {
    if (!editingDocket || !postNoteText.trim()) return;
    setPostNoteSubmitting(true);
    try {
      const res = await api.post(`/dockets/${editingDocket.id}/notes`, { text: postNoteText.trim() });
      setEditingDocket(prev => ({
        ...prev,
        post_submission_notes: [...(prev.post_submission_notes || []), res.data],
      }));
      setPostNoteText('');
      toast.success('Post-submission note added');
      // Refresh the background list so the note persists on drawer re-open
      docketsList.reload();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add note'));
    } finally {
      setPostNoteSubmitting(false);
    }
  };


  // Restore docket to a previous version from edit history
  const handleRestoreVersion = async () => {
    if (!editingDocket || !restoreTarget) return;
    setRestoring(true);
    try {
      const res = await api.post(`/dockets/${editingDocket.id}/restore/${restoreTarget.id}`);
      toast.success('Docket restored to previous version');
      // Refresh the drawer with the restored data
      setEditingDocket(res.data);
      setEditItems(res.data.items?.map(item => ({ ...item })) || []);
      setEditNotes(res.data.notes || '');
      setEditWorkCompleted(res.data.work_completed || '');
      setEditMaterialLoads(res.data.material_loads?.map(l => ({ ...l })) || []);
      editBaselineRef.current = serializeEditable(
        res.data.items?.map(i => ({ ...i })) || [],
        res.data.notes || '',
        res.data.work_completed || '',
        res.data.material_loads?.map(l => ({ ...l })) || [],
        !!res.data.suppress_fuel_levy
      );
      setRestoreTarget(null);
      fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to restore docket version'));
    } finally {
      setRestoring(false);
    }
  };


  // Save any in-progress edits, then run a workflow action (confirm/submit).
  const handleSaveThen = async (action) => {
    setSaving(true);
    try {
      await persistEdits();
      setEditReason('');
      setIsEditing(false);
      setEditDialogOpen(false);
      await action();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save docket'));
    } finally {
      setSaving(false);
    }
  };

  // Recalculate docket items using submitted data and price list
  const handleRecalculatePrices = async () => {
    if (!editingDocket) return;
    setRecalculating(true);
    try {
      const response = await api.post(`/dockets/${editingDocket.id}/recalculate-prices`);
      const recalculatedItems = response.data.items || [];
      const priceListName = response.data.price_list_name;
      
      setEditItems(recalculatedItems);
      setFuelLevyRemoved(false);
      setUsedPriceList(priceListName);
      toast.success(`Prices recalculated using "${priceListName}"`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to recalculate prices'));
    } finally {
      setRecalculating(false);
    }
  };

  // Live fuel-levy recompute: keeps the Fuel Levy line in sync while editing invoice lines
  useEffect(() => {
    if (!editDialogOpen) return;
    if (!tenantSettings.fuel_levy_enabled || !(tenantSettings.fuel_levy_percentage > 0)) return;
    // Admin removed the auto Fuel Levy for this docket — don't re-add it.
    if (fuelLevyRemoved) {
      setEditItems(prev => prev.some(i => i.item_type === 'fuel_levy')
        ? prev.filter(i => i.item_type !== 'fuel_levy')
        : prev);
      return;
    }
    const pct = parseFloat(tenantSettings.fuel_levy_percentage) || 0;
    const basis = tenantSettings.fuel_levy_basis || 'invoice_total';
    const nonLevy = editItems.filter(i => i.item_type !== 'fuel_levy');
    const base = nonLevy.reduce((sum, i) => {
      if (basis === 'asset_only' && i.item_type !== 'equipment') return sum;
      return sum + (parseFloat(i.billed_qty ?? i.qty) || 0) * (parseFloat(i.unit_price) || 0);
    }, 0);
    const amount = Math.round(base * pct) / 100;
    const existing = editItems.find(i => i.item_type === 'fuel_levy');
    const pctLabel = Number.isInteger(pct) ? pct : pct;
    if (amount > 0) {
      if (!existing || Math.abs((existing.unit_price || 0) - amount) > 0.001) {
        const levyLine = {
          id: existing?.id || `fuel-levy-${Date.now()}`,
          item_type: 'fuel_levy',
          type_name: `Fuel Levy (${pctLabel}%)`,
          qty: 1,
          uom: 'EACH',
          unit_price: amount,
          account_item_id: tenantSettings.fuel_levy_account_id || '',
          rule_generated: true,
        };
        setEditItems(prev => [...prev.filter(i => i.item_type !== 'fuel_levy'), levyLine]);
      }
    } else if (existing) {
      setEditItems(prev => prev.filter(i => i.item_type !== 'fuel_levy'));
    }
  }, [editItems, tenantSettings, editDialogOpen, fuelLevyRemoved]);


  // Submit for Approval
  const handleSubmitForApproval = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/submit-for-approval`);
      toast.success('Docket submitted for approval');
      await fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit for approval'));
    } finally {
      setActionLoading(null);
    }
  };

  // Approve Docket
  const handleApproveDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/approve`);
      toast.success('Docket approved');
      await fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to approve docket'));
    } finally {
      setActionLoading(null);
    }
  };

  // Confirm Docket (one-click: draft -> approved, when Skip Approval rule is on)
  const handleConfirmDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/approve`);
      toast.success('Docket confirmed');
      await fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to confirm docket'));
    } finally {
      setActionLoading(null);
    }
  };

  // Reject Docket
  const handleRejectDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/reject`);
      toast.success('Docket returned to draft');
      await fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reject docket'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/reset`);
      toast.success('Docket reset to draft');
      await fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reset docket'));
    } finally {
      setActionLoading(null);
    }
  };

  // Email Docket to Customer
  const handleOpenEmailDialog = async (docket) => {
    setEmailDocket(docket);
    setEmailDialogOpen(true);
    setEmailLoading(true);
    setEmailRecipients([]);
    setEmailCustomInput('');
    try {
      const response = await api.get(`/dockets/${docket.id}/customer-email`);
      const seen = new Set();
      const list = [];
      const addRecip = (email, name, source) => {
        if (!email) return;
        const k = email.toLowerCase();
        if (seen.has(k)) return;
        seen.add(k);
        list.push({ email, name: name || email, source, checked: true });
      };
      // Docket-to email (preferred) then customer main email
      const docketToEmail = (response.data.docket_to_email || '').trim();
      const custEmail = (response.data.customer_email || '').trim();
      if (docketToEmail) {
        addRecip(docketToEmail, response.data.contact_name || response.data.customer_name, 'Docket-To Email');
      } else if (custEmail) {
        addRecip(custEmail, response.data.contact_name || response.data.customer_name, 'Customer');
      }
      // Customer contacts
      for (const c of (response.data.customer_contacts || [])) {
        addRecip(c.email, c.name + (c.position ? ` (${c.position})` : ''), 'Contact');
      }
      // Site contacts
      for (const c of (response.data.site_contacts || [])) {
        addRecip(c.email, c.name + (c.role ? ` (${c.role})` : ''), 'Site Contact');
      }
      setEmailRecipients(list);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setEmailRecipients([]);
    } finally {
      setEmailLoading(false);
    }
  };

  const toggleEmailRecipient = (email) => {
    setEmailRecipients(prev => prev.map(r => r.email === email ? { ...r, checked: !r.checked } : r));
  };

  const addCustomEmailRecipient = () => {
    const e = emailCustomInput.trim();
    if (!e) return;
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(e)) { toast.error('Invalid email address'); return; }
    if (emailRecipients.some(r => r.email.toLowerCase() === e.toLowerCase())) {
      toast.error('Email already in the list');
      return;
    }
    setEmailRecipients(prev => [...prev, { email: e, name: e, source: 'Custom', checked: true }]);
    setEmailCustomInput('');
  };

  const removeEmailRecipient = (email) => {
    setEmailRecipients(prev => prev.filter(r => r.email !== email));
  };

  const getSelectedEmails = () => emailRecipients.filter(r => r.checked).map(r => r.email);

  // ---- Send Invoice To Customer ----
  const handleOpenInvoiceDialog = async (docket) => {
    setInvoiceDocket(docket);
    setInvoiceDialogOpen(true);
    setInvoiceLoading(true);
    setInvoicePreview(null);
    setInvoiceRecipients(['']);
    setInvoiceSubject('');
    setInvoiceBody('');
    try {
      const { data } = await api.get(`/dockets/${docket.id}/invoice-email-preview`);
      setInvoicePreview(data);
      setInvoiceSubject(data.subject || '');
      setInvoiceBody(data.body || '');
      const preset = data.invoice_sent_to && data.invoice_sent_to.length
        ? data.invoice_sent_to
        : (data.customer_email ? [data.customer_email] : ['']);
      setInvoiceRecipients(preset.length ? preset : ['']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load invoice details');
      setInvoiceDialogOpen(false);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const updateInvoiceRecipient = (idx, value) => {
    setInvoiceRecipients(prev => prev.map((r, i) => (i === idx ? value : r)));
  };
  const addInvoiceRecipient = () => setInvoiceRecipients(prev => [...prev, '']);
  const removeInvoiceRecipient = (idx) => setInvoiceRecipients(prev => prev.filter((_, i) => i !== idx));

  const handleSendInvoice = async () => {
    if (!invoiceDocket) return;
    const recipients = invoiceRecipients.map(r => r.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient email address');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const bad = recipients.find(r => !emailRe.test(r));
    if (bad) { toast.error(`Invalid email address: ${bad}`); return; }

    setInvoiceSending(true);
    try {
      const { data } = await api.post(`/dockets/${invoiceDocket.id}/send-invoice`, {
        recipients,
        subject: invoiceSubject,
        body: invoiceBody,
      });
      toast.success(data.message || 'Invoice sent to customer');
      setInvoiceDialogOpen(false);
      fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setInvoiceSending(false);
    }
  };

  const handleDownloadInvoicePdf = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/invoice-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open the invoice PDF');
    }
  };


  const handleSendDocketEmail = async () => {
    if (!emailDocket) return;
    
    const selected = getSelectedEmails();
    if (selected.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    
    setEmailSending(true);
    try {
      await api.post(`/dockets/${emailDocket.id}/email`, {
        to_emails: selected
      });
      const label = selected.length === 1 ? selected[0] : `${selected.length} recipients`;
      toast.success(`Docket queued for delivery to ${label}`, {
        description: 'PDF is being generated and emailed now'
      });
      setEmailDialogOpen(false);
      setEmailDocket(null);
      setEmailRecipients([]);
      setEmailCustomInput('');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send docket email'));
    } finally {
      setEmailSending(false);
    }
  };

  // ---- Request Signature (remote) ----
  const handleOpenSignatureDialog = async (docket) => {
    setSigDocket(docket);
    setSigDialogOpen(true);
    setSigLoading(true);
    setSigMode('customer');
    setSigSelectedContact('');
    setSigCustomEmail('');
    setSigContacts({ customer_contacts: [], site_contacts: [] });
    try {
      const response = await api.get(`/dockets/${docket.id}/customer-email`);
      setSigAddress(response.data.customer_email || '');
      setSigContacts({
        customer_contacts: response.data.customer_contacts || [],
        site_contacts: response.data.site_contacts || [],
      });
      if (!response.data.customer_email) {
        const allContacts = [...(response.data.customer_contacts || []), ...(response.data.site_contacts || [])];
        if (allContacts.length > 0) {
          setSigMode('contact');
          setSigSelectedContact(allContacts[0].email);
        } else {
          setSigMode('custom');
        }
      }
    } catch (error) {
      setSigAddress('');
      setSigMode('custom');
    } finally {
      setSigLoading(false);
    }
  };

  const getResolvedSigEmail = () => {
    if (sigMode === 'customer') return sigAddress;
    if (sigMode === 'contact') return sigSelectedContact;
    return sigCustomEmail;
  };

  const handleRequestSignature = async () => {
    if (!sigDocket) return;
    const resolved = (getResolvedSigEmail() || '').trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(resolved)) {
      toast.error('Please choose or enter a valid email address');
      return;
    }
    setSigSending(true);
    try {
      const { data } = await api.post(`/dockets/${sigDocket.id}/request-signature`, { to_email: resolved });
      toast.success(data.message || `Signature request sent to ${resolved}`, {
        description: `Link expires in ${data.expiry_hours || 24} hours`,
      });
      setSigDialogOpen(false);
      setSigDocket(null);
      fetchDockets();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send signature request'));
    } finally {
      setSigSending(false);
    }
  };

  // Fetch Email Status
  const handleViewEmailStatus = async (docket) => {
    setEmailDocket(docket);
    setEmailStatusDialogOpen(true);
    setEmailStatusLoading(true);
    try {
      const response = await api.get(`/dockets/${docket.id}/email-status`);
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
      setEmailStatus(null);
      toast.error('Failed to fetch email status');
    } finally {
      setEmailStatusLoading(false);
    }
  };

  // Resend Docket Email
  const handleResendDocketEmail = async () => {
    if (!emailDocket) return;
    setEmailSending(true);
    try {
      const response = await api.post(`/dockets/${emailDocket.id}/resend-email`);
      toast.success(`Docket resent to ${response.data.sent_to}`, {
        description: 'PDF docket has been resent to the customer'
      });
      // Refresh email status
      const statusResponse = await api.get(`/dockets/${emailDocket.id}/email-status`);
      setEmailStatus(statusResponse.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to resend docket email'));
    } finally {
      setEmailSending(false);
    }
  };

  // Get status badge color
  const getEmailStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'opened':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'clicked':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'bounced':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'spam':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'delayed':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchDockets();
      toast.success('Dockets refreshed');
    } catch (error) {
      toast.error('Failed to refresh dockets');
    } finally {
      setRefreshing(false);
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      let detail = 'Failed to generate docket PDF';
      try {
        const data = error.response?.data;
        if (data instanceof Blob) {
          const parsed = JSON.parse(await data.text());
          if (parsed?.detail) detail = parsed.detail;
        } else if (data?.detail) {
          detail = data.detail;
        }
      } catch (_) { /* keep default */ }
      toast.error(detail);
    }
  };

  // Download PDF (forces a file download from the drawer)
  const handleDownloadPDF = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `docket_${docket.docket_number || docket.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      let detail = 'Failed to download docket PDF';
      try {
        const data = error.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } else if (data?.detail) {
          detail = data.detail;
        }
      } catch (_) { /* keep default */ }
      toast.error(detail);
    }
  };

  // Export to XERO
  const handleExportToXero = (docket) => {
    if (docket.status !== 'approved') {
      toast.error('Only approved dockets can be exported to Xero');
      return;
    }
    if (docket.xero_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      return;
    }
    setSelectedDocket(docket);
    setXeroDialogOpen(true);
  };

  // Multi-accounting: resolve which connection of `provider` a docket's customer
  // should export to. Returns:
  //   { connectionId } → push directly (may be undefined → backend default)
  //   { choice: true } → a "choose system" dialog was opened; caller should stop
  //   { blocked: true } → don't sync / error; caller should stop
  const resolveTargetConnection = async ({ provider, customerId, single, docketId, docketIds, platformPath, platformLabel }) => {
    let data;
    try {
      const res = await api.get(`/accounting/resolve-customer-export/${customerId}`);
      data = res.data;
    } catch (e) {
      // Resolver unavailable → fall back to the backend default routing.
      return { connectionId: undefined };
    }
    if (data.status === 'disabled') {
      toast.error('This customer is set to "Don\'t sync". Update its accounting system on the Customers page to export.');
      return { blocked: true };
    }
    const provConns = (data.connections || []).filter(c => c.provider === provider && c.status !== 'disconnected');
    // Already linked to a connection of THIS provider → use it.
    const linked = data.connection && data.connection.provider === provider
      ? provConns.find(c => c.id === data.connection.id)
      : null;
    if (linked) return { connectionId: linked.id };
    if (provConns.length <= 1) return { connectionId: provConns[0]?.id };
    // 2+ connections of this provider and no explicit link → prompt to choose.
    setPendingPush({ provider, customerId, single, docketId, docketIds, platformPath, platformLabel });
    setConnChoiceConnections(provConns);
    setConnChoiceSelected((provConns.find(c => c.is_default) || provConns[0]).id);
    setConnChoiceOpen(true);
    return { choice: true };
  };

  const doPushSingle = async (platformPath, platformLabel, docketId, connectionId) => {
    const body = { docket_id: docketId };
    if (connectionId) body.connection_id = connectionId;
    const response = await api.post(`/${platformPath}/push-invoice`, body);
    const num = response.data.invoice_number || response.data.xero_invoice_number
      || response.data.myob_invoice_number || response.data.qbo_invoice_number;
    toast.success(num ? `Invoice ${num} created in ${platformLabel}` : `Invoice created in ${platformLabel}`);
    fetchDockets();
  };

  // Confirm the "choose accounting system" prompt: link the customer to the
  // chosen connection (remembered for next time), then run the pending push.
  const confirmConnChoice = async () => {
    if (!pendingPush || !connChoiceSelected) return;
    const p = pendingPush;
    const connId = connChoiceSelected;
    setConnChoiceBusy(true);
    try {
      try {
        await api.post(`/customers/${p.customerId}/accounting-link`, { connection_id: connId });
      } catch (e) {
        // Non-fatal: linking is a convenience; continue with the push regardless.
      }
      if (p.single) {
        await doPushSingle(p.platformPath, p.platformLabel, p.docketId, connId);
        if (p.provider === 'xero') setXeroDialogOpen(false);
        else if (p.provider === 'myob') setMyobDialogOpen(false);
        else setQboDialogOpen(false);
      } else {
        const res = await api.post(`/${p.platformPath}/group-push-invoice`, { docket_ids: p.docketIds, connection_id: connId });
        toast.success(res.data.message || `Grouped invoice created in ${p.platformLabel}`);
        setGroupExportOpen(false);
        setSelectedDockets([]);
        fetchDockets();
      }
      setConnChoiceOpen(false);
      setPendingPush(null);
    } catch (error) {
      showExportFailureToast(error, p.platformLabel);
    } finally {
      setConnChoiceBusy(false);
    }
  };

  // Push docket to Xero
  const handlePushToXero = async () => {
    if (!selectedDocket) return;
    
    // Double-check to prevent duplicates
    if (selectedDocket.xero_invoice_id || selectedDocket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      setXeroDialogOpen(false);
      return;
    }
    
    setXeroPushing(true);
    try {
      const r = await resolveTargetConnection({
        provider: 'xero', customerId: selectedDocket.customer_id, single: true,
        docketId: selectedDocket.id, platformPath: 'xero', platformLabel: 'Xero',
      });
      if (r.choice || r.blocked) return;
      await doPushSingle('xero', 'Xero', selectedDocket.id, r.connectionId);
      setXeroDialogOpen(false);
    } catch (error) {
      showExportFailureToast(error, 'Xero');
    } finally {
      setXeroPushing(false);
    }
  };

  // Export to MYOB
  const handleExportToMyob = (docket) => {
    if (docket.status !== 'approved') {
      toast.error('Only approved dockets can be exported to MYOB');
      return;
    }
    if (docket.myob_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      return;
    }
    setSelectedDocket(docket);
    setMyobDialogOpen(true);
  };

  // Push docket to MYOB
  const handlePushToMyob = async () => {
    if (!selectedDocket) return;
    
    // Double-check to prevent duplicates
    if (selectedDocket.myob_invoice_id || selectedDocket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      setMyobDialogOpen(false);
      return;
    }
    
    setMyobPushing(true);
    try {
      const r = await resolveTargetConnection({
        provider: 'myob', customerId: selectedDocket.customer_id, single: true,
        docketId: selectedDocket.id, platformPath: 'myob', platformLabel: 'MYOB',
      });
      if (r.choice || r.blocked) return;
      await doPushSingle('myob', 'MYOB', selectedDocket.id, r.connectionId);
      setMyobDialogOpen(false);
    } catch (error) {
      showExportFailureToast(error, 'MYOB');
    } finally {
      setMyobPushing(false);
    }
  };

  // QuickBooks export
  const [qboDialogOpen, setQboDialogOpen] = useState(false);
  const [qboPushing, setQboPushing] = useState(false);

  const handleExportToQbo = (docket) => {
    if (docket.status !== 'approved') {
      toast.error('Only approved dockets can be exported to QuickBooks');
      return;
    }
    if (docket.qbo_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to QuickBooks');
      return;
    }
    setSelectedDocket(docket);
    setQboDialogOpen(true);
  };

  const handlePushToQbo = async () => {
    if (!selectedDocket) return;
    
    if (selectedDocket.qbo_invoice_id || selectedDocket.status === 'exported') {
      toast.error('This docket has already been exported to QuickBooks');
      setQboDialogOpen(false);
      return;
    }
    
    setQboPushing(true);
    try {
      const r = await resolveTargetConnection({
        provider: 'quickbooks', customerId: selectedDocket.customer_id, single: true,
        docketId: selectedDocket.id, platformPath: 'quickbooks', platformLabel: 'QuickBooks',
      });
      if (r.choice || r.blocked) return;
      await doPushSingle('quickbooks', 'QuickBooks', selectedDocket.id, r.connectionId);
      setQboDialogOpen(false);
    } catch (error) {
      showExportFailureToast(error, 'QuickBooks');
    } finally {
      setQboPushing(false);
    }
  };

  const canApprove = user?.role === 'admin' || user?.role === 'dispatcher';

  const getStatusBadge = (status, docket = null) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-700">Draft</Badge>;
      case 'submitted':
        return <Badge variant="secondary" className="bg-cyan-100 text-cyan-700">Submitted</Badge>;
      case 'pending_approval':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-700">Pending Approval</Badge>;
      case 'approved':
        return <Badge variant="secondary" className="bg-green-100 text-green-700">Approved</Badge>;
      case 'exported':
        // Check which accounting package it was exported to
        if (docket?.xero_invoice_id) {
          return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Exported to Xero</Badge>;
        } else if (docket?.myob_invoice_id) {
          return <Badge variant="secondary" className="bg-purple-100 text-purple-700">Exported to MYOB</Badge>;
        }
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Exported</Badge>;
      case 'invoiced':
        return <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">Invoiced</Badge>;
      case 'sent_to_customer':
        return <Badge variant="secondary" className="bg-teal-100 text-teal-700">Sent To Customer</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  // OCR Handlers
  const handleOpenOcrDialog = () => {
    if (!tenantSettings.openai_api_key) {
      toast.error('OpenAI API key not configured. Please add your API key in Settings > AI Integration.');
      return;
    }
    setOcrImage(null);
    setOcrImagePreview(null);
    setOcrResult(null);
    setOcrDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setOcrImage(file);
      setOcrResult(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setOcrImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOcrExtract = async () => {
    if (!ocrImage) {
      toast.error('Please select an image first');
      return;
    }

    setOcrLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', ocrImage);

      const response = await api.post('/dockets/ocr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setOcrResult(response.data.extracted);
        toast.success('Docket data extracted successfully!');
      } else {
        toast.error(response.data.error || 'Failed to extract docket data');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to extract docket data'));
    } finally {
      setOcrLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    // Format based on tenant settings
    const format = tenantSettings.date_format || 'DD/MM/YYYY';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (format) {
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY': return `${day}-${monthShort}-${year}`;
      default: return `${day}/${month}/${year}`;
    }
  };

  const calculateSubtotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.qty) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const calculateTax = (subtotal) => {
    const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
    return subtotal * (taxRate / 100);
  };

  const calculateTotal = (items) => {
    const subtotal = calculateSubtotal(items);
    return subtotal + calculateTax(subtotal);
  };

  // Get date range based on filter - memoized
  const getDateFilterRange = useCallback(() => {
    return { from: dateFrom, to: dateTo };
  }, [dateFrom, dateTo]);

  // Filter dockets - MEMOIZED for performance
  // Date filtering is now handled server-side via dateFrom/dateTo params.
  // Client-side filtering only applies search and status.
  const filteredDockets = useMemo(() => {
    return dockets.filter(d => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        d.customer_name?.toLowerCase().includes(searchLower) ||
        d.site_name?.toLowerCase().includes(searchLower) ||
        d.equipment_type?.toLowerCase().includes(searchLower) ||
        d.job_description?.toLowerCase().includes(searchLower);
      
      const matchesStatus = statusFilter.size === 0 || statusFilter.has(d.status);
      
      return matchesSearch && matchesStatus;
    });
  }, [dockets, searchTerm, statusFilter]);

  // Sortable columns
  const docketColumnAccessors = useMemo(() => ({
    docket_number: (d) => d.docket_number ? parseInt(d.docket_number, 10) || d.docket_number : null,
    date: 'job_date',
    customer: (d) => d.customer_name?.toLowerCase(),
    site: (d) => d.site_name?.toLowerCase(),
    asset: (d) => (d.equipment_name || d.equipment_type || '')?.toLowerCase(),
    items: (d) => d.items?.length || 0,
    total: (d) => d.items?.reduce((sum, it) => sum + (parseFloat(it.billed_qty ?? it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0) || 0,
    invoice: (d) => d.invoice_number || '',
    status: 'status',
  }), []);

  const { sortedData: sortedDockets, requestSort, getSortDirection } = useSortableTable(filteredDockets, docketColumnAccessors);

  if ((loading || docketsList.loading) && dockets.length === 0) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="dockets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Dockets</h1>
          <p className="text-muted-foreground">View and manage job dockets for invoicing</p>
        </div>
        {isAiAvailable() && (
          <Button 
            onClick={handleOpenOcrDialog} 
            className="btn-volt"
            data-testid="scan-docket-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            Scan Docket
            <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700 text-xs">AI</Badge>
          </Button>
        )}
      </div>

      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <ListViewHeader
            count={filteredDockets.length}
            countLabel="Dockets"
            onRefresh={handleRefresh}
            refreshing={refreshing}
            
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search dockets..."
            searchTestId="docket-search"
            
            showStatusFilter={false}
            
            dateFilter={dateFilter}
            onDateFilterChange={handleDateFilterChange}
            dateFilterOptions={[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'last_week', label: 'Last Week' },
              { value: 'this_week', label: 'This Week' },
              { value: 'all', label: 'All' },
            ]}
            
            showCustomDateRange
            alwaysShowDateRange
            dateFrom={dateFrom}
            onDateFromChange={handleDateFromChange}
            dateTo={dateTo}
            onDateToChange={handleDateToChange}
            formatDateDisplay={formatDate}
          >
            <div className="flex items-center gap-2">
              <Label className="label-overline text-xs">Status</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-[180px] h-8 text-sm justify-between" data-testid="docket-status-filter">
                    <span className="flex items-center gap-1.5 truncate">
                      <Filter className="h-3.5 w-3.5 shrink-0" />
                      {statusFilter.size === 0 ? 'All' : statusFilter.size === 1 ? [...statusFilter].map(s => DOCKET_STATUS_OPTIONS.find(o => o.value === s)?.label).join('') : `${statusFilter.size} selected`}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="start">
                  <div className="space-y-1">
                    {DOCKET_STATUS_OPTIONS.map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox
                          checked={statusFilter.has(opt.value)}
                          onCheckedChange={() => toggleStatus(opt.value)}
                          data-testid={`docket-status-${opt.value}`}
                        />
                        {opt.label}
                      </label>
                    ))}
                    {statusFilter.size > 0 && (
                      <>
                        <div className="border-t my-1" />
                        <button
                          className="w-full text-xs text-muted-foreground hover:text-foreground px-2 py-1 text-left"
                          onClick={() => setStatusFilter(new Set())}
                        >
                          Clear all
                        </button>
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </ListViewHeader>
        </CardHeader>
        <CardContent>
          {selectedDockets.length > 0 && (
            <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between gap-3 flex-wrap" data-testid="docket-bulk-bar">
              <span className="text-sm" data-testid="docket-bulk-selected-count">{selectedDockets.length} docket(s) selected</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelectedDockets([])} data-testid="docket-bulk-clear-btn">
                  Clear
                </Button>
                {selectedDockets.length >= 2 && (() => {
                  const gi = getGroupExportInfo();
                  const reason = !gi.sameCustomer
                    ? 'All selected dockets must be for the same customer'
                    : (!gi.allApproved ? 'Only approved, not-yet-exported dockets can be grouped' : '');
                  return (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!gi.eligible}
                      title={reason || 'Group selected dockets into one invoice'}
                      onClick={() => setGroupExportOpen(true)}
                      data-testid="docket-group-export-btn"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Group &amp; Export ({selectedDockets.length})
                    </Button>
                  );
                })()}
                <Button size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)} data-testid="docket-bulk-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedDockets.length})
                </Button>
              </div>
            </div>
          )}
          {filteredDockets.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {dockets.length === 0 
                  ? 'No dockets yet. Create dockets from confirmed jobs in Jobs & Schedule.'
                  : 'No dockets match your search'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={sortedDockets.length > 0 && sortedDockets.every(d => selectedDockets.includes(d.id))}
                        onCheckedChange={(checked) => {
                          const ids = sortedDockets.map(d => d.id);
                          setSelectedDockets(checked ? ids : []);
                        }}
                        data-testid="docket-select-all"
                      />
                    </TableHead>
                    <SortableTableHead sortKey="docket_number" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Docket #</SortableTableHead>
                    <SortableTableHead sortKey="date" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Date</SortableTableHead>
                    <SortableTableHead sortKey="customer" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Customer</SortableTableHead>
                    <SortableTableHead sortKey="site" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Site</SortableTableHead>
                    <SortableTableHead sortKey="asset" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Asset</SortableTableHead>
                    <SortableTableHead sortKey="items" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Items</SortableTableHead>
                    {canViewFinancial && (
                      <SortableTableHead sortKey="total" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline text-right">Total</SortableTableHead>
                    )}
                    {canViewFinancial && (
                      <SortableTableHead sortKey="invoice" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Invoice #</SortableTableHead>
                    )}
                    <SortableTableHead sortKey="status" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Status</SortableTableHead>
                    <TableHead className="label-overline text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedDockets.map((docket) => (
                    <TableRow 
                      key={docket.id} 
                      data-testid={`docket-row-${docket.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={(e) => {
                        // Don't trigger if clicking actions menu
                        if (e.target.closest('button')) return;
                        // Always open the tabbed drawer (Docket + Invoice tabs)
                        handleOpenEditDialog(docket);
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedDockets.includes(docket.id)}
                          onCheckedChange={() => toggleSelectDocket(docket.id)}
                          data-testid={`docket-select-${docket.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-bold text-amber-600 dark:text-[#ffd108]">
                        {docket.docket_number ? `#${docket.docket_number}` : '-'}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatDate(docket.job_date)}
                      </TableCell>
                      <TableCell>
                        {docket.customer_name}
                        {docket.po_number && (
                          <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`docket-po-${docket.id}`}>
                            PO: {docket.po_number}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{docket.site_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{docket.equipment_type}</Badge>
                        {(() => {
                          const labels = getDocketComboLabels(docket);
                          if (!labels.length) return null;
                          return (
                            <div
                              className="mt-1 flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-300"
                              title={labels.join(' + ')}
                              data-testid={`docket-combo-${docket.id}`}
                            >
                              <Layers className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[180px]">{labels.join(' + ')}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{docket.items?.length || 0} items</Badge>
                      </TableCell>
                      {canViewFinancial && (
                        <TableCell className="text-right font-mono">
                          {tenantSettings.currency_symbol}{calculateTotal(docket.items).toFixed(2)}
                        </TableCell>
                      )}
                      {canViewFinancial && (
                      <TableCell className="font-mono text-sm">
                        {docket.invoice_number || (docket.status === 'exported' ? (docket.xero_invoice_number || docket.myob_invoice_number || docket.qbo_invoice_number) : '-')}
                        {docket.grouped_invoice && (docket.group_docket_ids?.length > 1) && (
                          <Badge
                            variant="outline"
                            className="mt-1 flex w-fit items-center gap-1 border-indigo-400 text-indigo-600 dark:text-indigo-300"
                            title={`Billed together on one invoice with ${docket.group_docket_ids.length} dockets${(() => {
                              const nums = docket.group_docket_ids
                                .map(id => dockets.find(d => d.id === id)?.docket_number)
                                .filter(Boolean)
                                .map(n => `#${n}`);
                              return nums.length ? `: ${nums.join(', ')}` : '';
                            })()}`}
                            data-testid={`docket-grouped-badge-${docket.id}`}
                          >
                            <Layers className="h-3 w-3" />
                            Grouped ({docket.group_docket_ids.length})
                          </Badge>
                        )}
                      </TableCell>
                      )}
                      <TableCell>
                        {getStatusBadge(docket.status || 'draft', docket)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEditDialog(docket)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {docket.status === 'draft' && (
                              tenantSettings.skip_docket_approval && canApprove ? (
                                <DropdownMenuItem onClick={() => handleConfirmDocket(docket.id)} className="text-green-600" data-testid={`confirm-docket-${docket.id}`}>
                                  <Check className="h-4 w-4 mr-2" />
                                  Confirm Docket
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleSubmitForApproval(docket.id)}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit for Approval
                                </DropdownMenuItem>
                              )
                            )}
                            {docket.status === 'pending_approval' && canApprove && (
                              <>
                                <DropdownMenuItem onClick={() => handleApproveDocket(docket.id)} className="text-green-600" data-testid={`approve-docket-${docket.id}`}>
                                  <Check className="h-4 w-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRejectDocket(docket.id)} className="text-amber-600">
                                  <X className="h-4 w-4 mr-2" />
                                  Return to Draft
                                </DropdownMenuItem>
                              </>
                            )}
                            {/* PDF & Email - available on all dockets */}
                            <DropdownMenuItem className="group/email" onClick={(e) => { e.stopPropagation(); handleOpenEmailDialog(docket); }} data-testid={`email-docket-${docket.id}`}>
                              <Mail className="h-4 w-4 mr-2 text-[#ffd108] group-hover/email:text-black" />
                              Email to Customer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewEmailStatus(docket); }} data-testid={`email-status-${docket.id}`}>
                              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                              View Email Status
                            </DropdownMenuItem>
                            {/* Request remote signature — when signature was skipped/not captured, and not yet remotely signed */}
                            {((docket.signature_skipped) || (docket.customer_signoff_confirmed && !docket.customer_signoff_signature)) && docket.signature_signed_via !== 'remote' && canApprove && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenSignatureDialog(docket); }} data-testid={`request-signature-${docket.id}`}>
                                <PenLine className="h-4 w-4 mr-2 text-amber-500" />
                                {docket.signature_request_status === 'requested' ? 'Resend Signature Request' : 'Request Signature'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleGeneratePDF(docket)}>
                              <FileDown className="h-4 w-4 mr-2" />
                              Download Docket PDF
                            </DropdownMenuItem>
                            {/* Send Invoice To Customer - financial access required */}
                            {canViewFinancial && isDocketExported(docket) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="group/inv"
                                  onClick={(e) => { e.stopPropagation(); handleOpenInvoiceDialog(docket); }}
                                  data-testid={`send-invoice-${docket.id}`}
                                >
                                  <Send className="h-4 w-4 mr-2 text-teal-600" />
                                  {docket.status === 'sent_to_customer' ? 'Re-send Invoice To Customer' : 'Send Invoice To Customer'}
                                </DropdownMenuItem>
                                {docket.status === 'sent_to_customer' && (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadInvoicePdf(docket); }} data-testid={`download-invoice-${docket.id}`}>
                                    <FileDown className="h-4 w-4 mr-2 text-teal-600" />
                                    Download Invoice PDF
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {/* Accounting exports - financial access required */}
                            {canViewFinancial && (docket.status === 'approved' || docket.status === 'exported') && (
                              <>
                                <DropdownMenuSeparator />
                                {docket.xero_invoice_id ? (
                                  <DropdownMenuItem disabled className="text-blue-600">
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Exported to Xero
                                  </DropdownMenuItem>
                                ) : docket.status !== 'exported' && tenantSettings.xero_connected && (
                                  <DropdownMenuItem onClick={() => handleExportToXero(docket)}>
                                    <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                                    Push to Xero
                                  </DropdownMenuItem>
                                )}
                                {/* MYOB Export Option */}
                                {docket.myob_invoice_id ? (
                                  <DropdownMenuItem disabled className="text-purple-600">
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Exported to MYOB
                                  </DropdownMenuItem>
                                ) : docket.status !== 'exported' && tenantSettings.myob_connected && (
                                  <DropdownMenuItem onClick={() => handleExportToMyob(docket)}>
                                    <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                                    Push to MYOB
                                  </DropdownMenuItem>
                                )}
                                {/* QuickBooks Export Option */}
                                {docket.qbo_invoice_id ? (
                                  <DropdownMenuItem disabled className="text-green-600">
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Exported to QuickBooks
                                  </DropdownMenuItem>
                                ) : docket.status !== 'exported' && tenantSettings.qbo_connected && (
                                  <DropdownMenuItem onClick={() => handleExportToQbo(docket)}>
                                    <ExternalLink className="h-4 w-4 mr-2 text-green-600" />
                                    Push to QuickBooks
                                  </DropdownMenuItem>
                                )}
                                {/* Show link to settings if no accounting connected */}
                                {!tenantSettings.xero_connected && !tenantSettings.myob_connected && !tenantSettings.qbo_connected && docket.status !== 'exported' && (
                                  <DropdownMenuItem onClick={() => window.location.href = '/settings?tab=accounting'}>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Connect Accounting
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {canApprove && (
                                  <DropdownMenuItem onClick={() => handleResetDocket(docket.id)} className="text-amber-600" data-testid={`reset-docket-${docket.id}`}>
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset Docket
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteDocket(docket.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Docket
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div ref={docketsList.sentinelRef} />
          {filteredDockets.length > 0 && (
            <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground" data-testid="dockets-pagination-footer">
              <span>Showing {filteredDockets.length} of {docketsList.total}</span>
              {docketsList.hasMore && (
                <Button size="sm" variant="outline" onClick={docketsList.loadMore} data-testid="dockets-load-more">Load more</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <Sheet open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading text-xl">Docket Details</SheetTitle>
              {selectedDocket && getStatusBadge(selectedDocket.status || 'draft')}
            </div>
          </SheetHeader>
          
          {selectedDocket && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedDocket.job_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {selectedDocket.customer_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {selectedDocket.site_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Asset</p>
                  <p className="font-medium flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    {selectedDocket.equipment_type}
                  </p>
                </div>
              </div>

              {selectedDocket.job_description && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Job Description</p>
                  <p className="text-sm">{selectedDocket.job_description}</p>
                </div>
              )}

              {/* Route Stops (read-only) — recorded depot & stop times for haulage/multi-stop jobs */}
              {(selectedDocket.job_stops?.length || selectedDocket.depot_departed_at || selectedDocket.depot_arrived_at) ? (
                (() => {
                  const tfmt = (iso) => {
                    if (!iso) return null;
                    const d = new Date(iso);
                    if (isNaN(d.getTime())) return (iso.split('T')[1] || '').slice(0, 5) || null;
                    const hh = String(d.getHours()).padStart(2, '0');
                    const mm = String(d.getMinutes()).padStart(2, '0');
                    return formatTime(`${hh}:${mm}`, tenantSettings.time_format);
                  };
                  const stops = (selectedDocket.job_stops || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
                  return (
                    <div data-testid="docket-route-stops">
                      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Route Stops
                        {selectedDocket.route_summary ? <span className="text-xs font-normal text-muted-foreground">— {selectedDocket.route_summary}</span> : null}
                      </h3>
                      {(selectedDocket.depot_departed_at || selectedDocket.depot_arrived_at) && (
                        <div className="flex items-center gap-6 text-sm mb-2 p-2 bg-muted/50 rounded flex-wrap" data-testid="docket-depot-times">
                          <span>Depot Depart: <b>{tfmt(selectedDocket.depot_departed_at) || '—'}</b>{selectedDocket.depot_departed_km != null ? ` (${selectedDocket.depot_departed_km} km)` : ''}</span>
                          <span>Depot Arrival: <b>{tfmt(selectedDocket.depot_arrived_at) || '—'}</b>{selectedDocket.depot_arrived_km != null ? ` (${selectedDocket.depot_arrived_km} km)` : ''}</span>
                          {selectedDocket.depot_departed_km != null && selectedDocket.depot_arrived_km != null && selectedDocket.depot_arrived_km >= selectedDocket.depot_departed_km && (
                            <span>Distance: <b>{selectedDocket.depot_arrived_km - selectedDocket.depot_departed_km} km</b></span>
                          )}
                        </div>
                      )}
                      {stops.length > 0 && (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="label-overline">#</TableHead>
                              <TableHead className="label-overline">Type</TableHead>
                              <TableHead className="label-overline">Location</TableHead>
                              <TableHead className="label-overline">ETA</TableHead>
                              <TableHead className="label-overline">Arrived</TableHead>
                              <TableHead className="label-overline">Status</TableHead>
                              <TableHead className="label-overline">Works</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {stops.map((s, i) => (
                              <TableRow key={s.id || i} data-testid={`docket-stop-row-${i}`}>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell className="capitalize">{s.type || 'stop'}</TableCell>
                                <TableCell className="max-w-[220px] truncate">{s.label || [s.address, s.suburb, s.state].filter(Boolean).join(', ') || '-'}</TableCell>
                                <TableCell>{s.est_arrival ? formatTime(s.est_arrival, tenantSettings.time_format) : '-'}</TableCell>
                                <TableCell className="font-medium">{tfmt(s.arrived_at) || '-'}</TableCell>
                                <TableCell className="capitalize">{s.status || '-'}</TableCell>
                                <TableCell className="max-w-[240px] text-xs whitespace-pre-wrap">{s.work_description || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  );
                })()
              ) : null}

              {/* Items Table */}
              <div>
                <h3 className="font-heading font-semibold mb-3">Docket Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {showItemCode && <TableHead className="label-overline">Item Code</TableHead>}
                      {showAccountId && <TableHead className="label-overline">Account ID</TableHead>}
                      <TableHead className="label-overline">Type</TableHead>
                      <TableHead className="label-overline">Description</TableHead>
                      <TableHead className="label-overline text-right">Qty</TableHead>
                      <TableHead className="label-overline">UOM</TableHead>
                      <TableHead className="label-overline text-right">Unit Price</TableHead>
                      <TableHead className="label-overline text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDocket.items?.filter(item => !item.included_in_asset).map((item, idx) => (
                      <TableRow key={item.id || idx} className={item.rule_generated ? 'bg-muted/30' : ''}>
                        {showItemCode && <TableCell className="font-mono text-xs">{item.item_code || '-'}</TableCell>}
                        {showAccountId && <TableCell className="font-mono text-xs">{item.account_item_id || '-'}</TableCell>}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                              className={`text-xs ${item.item_type === 'material' ? 'bg-orange-500/20 text-orange-400' : item.item_type === 'worker' ? 'bg-blue-600 text-white' : item.item_type === 'toll' ? 'bg-purple-500/20 text-purple-400' : ''}`}
                            >
                              {item.item_type === 'equipment' ? 'Equip' : item.item_type === 'worker' ? 'Labour' : item.item_type === 'material' ? 'Material' : item.item_type === 'toll' ? 'Toll' : 'Attach'}
                            </Badge>
                            {item.rule_generated && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-500">Auto</Badge>
                            )}
                            {item.type_name}
                          </div>
                          {item.rule_warning && (
                            <p className="text-[10px] text-amber-500 mt-0.5 ml-1">{item.rule_warning}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[300px] whitespace-pre-wrap break-words">
                          {item.description || '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.billed_qty && item.billed_qty !== item.qty ? (
                            <span title={`Actual: ${item.qty}, Billed: ${item.billed_qty} (min hire)`}>{item.billed_qty}</span>
                          ) : item.qty}
                        </TableCell>
                        <TableCell>{item.uom}</TableCell>
                        <TableCell className="text-right font-mono">
                          {tenantSettings.currency_symbol}{parseFloat(item.unit_price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {tenantSettings.currency_symbol}{(parseFloat(item.billed_qty ?? item.qty ?? 0) * parseFloat(item.unit_price || 0)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Totals with Tax */}
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="w-64 space-y-2">
                    {(() => {
                      const subtotal = calculateSubtotal(selectedDocket.items);
                      const taxAmount = calculateTax(subtotal);
                      const total = subtotal + taxAmount;
                      const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                          </div>
                          {taxRate > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t">
                            <span className="font-semibold">Total</span>
                            <span className="text-xl font-bold font-mono text-amber-600 dark:text-[#ffd108]">
                              {tenantSettings.currency_symbol}{total.toFixed(2)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Times section — show start/finish (and depot times when present) for non-rule-generated items */}
              {(() => {
                const timeItems = (selectedDocket?.items || []).filter(
                  i => !i.rule_generated && (i.item_type === 'equipment' || i.item_type === 'worker') && (i.start_time || i.finish_time)
                );
                if (timeItems.length === 0) return null;
                const anyDepot = timeItems.some(hasDepotTimes);
                return (
                  <div className="p-3 border rounded-lg">
                    <p className="label-overline text-xs mb-2">Times</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="text-left py-1 pr-2 font-medium">Resource</th>
                            {anyDepot ? (
                              <>
                                <th className="text-center py-1 px-1 font-medium">Depart Depot</th>
                                <th className="text-center py-1 px-1 font-medium">Arrive Site</th>
                                <th className="text-center py-1 px-1 font-medium">Depart Site</th>
                                <th className="text-center py-1 px-1 font-medium">Arrive Depot</th>
                              </>
                            ) : (
                              <>
                                <th className="text-center py-1 px-1 font-medium">Start</th>
                                <th className="text-center py-1 px-1 font-medium">Finish</th>
                              </>
                            )}
                            <th className="text-center py-1 px-1 font-medium">Lunch</th>
                            <th className="text-center py-1 px-1 font-medium">Travel</th>
                            <th className="text-right py-1 pl-1 font-medium">Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timeItems.map((item, idx) => (
                            <tr key={item.id || idx} className="border-b last:border-0">
                              <td className="py-1 pr-2">{item.display_name || item.type_name}</td>
                              {anyDepot ? (
                                <>
                                  <td className="text-center py-1 px-1 font-mono">{item.depart_depot_time ? formatTime(item.depart_depot_time, tenantSettings.time_format) : '-'}</td>
                                  <td className="text-center py-1 px-1 font-mono">{item.start_time ? formatTime(item.start_time, tenantSettings.time_format) : '-'}</td>
                                  <td className="text-center py-1 px-1 font-mono">{item.finish_time ? formatTime(item.finish_time, tenantSettings.time_format) : '-'}</td>
                                  <td className="text-center py-1 px-1 font-mono">{item.arrive_depot_time ? formatTime(item.arrive_depot_time, tenantSettings.time_format) : '-'}</td>
                                </>
                              ) : (
                                <>
                                  <td className="text-center py-1 px-1 font-mono">{item.start_time ? formatTime(item.start_time, tenantSettings.time_format) : '-'}</td>
                                  <td className="text-center py-1 px-1 font-mono">{item.finish_time ? formatTime(item.finish_time, tenantSettings.time_format) : '-'}</td>
                                </>
                              )}
                              <td className="text-center py-1 px-1">{item.lunch_minutes ? `${item.lunch_minutes}m` : '-'}</td>
                              <td className="text-center py-1 px-1">
                                {item.travel_out_minutes != null && item.travel_return_minutes != null
                                  ? `${item.travel_out_minutes} + ${item.travel_return_minutes} min`
                                  : item.travel_minutes ? `${item.travel_minutes} min` : '-'}
                              </td>
                              <td className="text-right py-1 pl-1 font-mono font-medium">{item.submitted_qty ?? item.qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {selectedDocket.notes && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Notes</p>
                  <p className="text-sm">{selectedDocket.notes}</p>
                </div>
              )}

              {/* Route Progress (haulage jobs) */}
              <RouteProgressList docket={selectedDocket} tenantSettings={tenantSettings} variant="bordered" api={api} onStopsChanged={(stops) => setSelectedDocket(prev => prev ? { ...prev, job_stops: stops } : prev)} />

              {/* Approval Info */}
              {selectedDocket.status === 'approved' && selectedDocket.approved_at && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                    Approved on {formatDate(selectedDocket.approved_at)}
                  </p>
                </div>
              )}
            </div>
          )}

          <SheetFooter className="pt-4 border-t mt-6 flex-wrap gap-2">
            <div className="flex gap-2 flex-1">
              {(selectedDocket?.status === 'approved' || selectedDocket?.status === 'exported') && (
                <>
                  <Button variant="outline" onClick={() => handleGeneratePDF(selectedDocket)}>
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF Invoice
                  </Button>
                  {tenantSettings.xero_connected && (
                    <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleExportToXero(selectedDocket); }}>
                      <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                      Export to Xero
                    </Button>
                  )}
                  {tenantSettings.myob_connected && (
                    <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleExportToMyob(selectedDocket); }}>
                      <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                      Export to MYOB
                    </Button>
                  )}
                  {tenantSettings.qbo_connected && (
                    <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleExportToQbo(selectedDocket); }}>
                      <ExternalLink className="h-4 w-4 mr-2 text-green-600" />
                      Export to QuickBooks
                    </Button>
                  )}
                  {canApprove && (
                    <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setViewDialogOpen(false); handleResetDocket(selectedDocket.id); }} data-testid="view-reset-docket-btn">
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Docket
                    </Button>
                  )}
                </>
              )}
              {selectedDocket?.status === 'draft' && (
                tenantSettings.skip_docket_approval && canApprove ? (
                  <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => { setViewDialogOpen(false); handleConfirmDocket(selectedDocket.id); }} data-testid="view-confirm-docket-btn">
                    <Check className="h-4 w-4 mr-2" />
                    Confirm Docket
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleSubmitForApproval(selectedDocket.id); }}>
                    <Send className="h-4 w-4 mr-2" />
                    Submit for Approval
                  </Button>
                )
              )}
              {selectedDocket?.status === 'pending_approval' && canApprove && (
                <>
                  <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => { setViewDialogOpen(false); handleApproveDocket(selectedDocket.id); }} data-testid="approve-docket-btn">
                    <Check className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setViewDialogOpen(false); handleRejectDocket(selectedDocket.id); }}>
                    <X className="h-4 w-4 mr-2" />
                    Return to Draft
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* OCR Scan Docket Dialog */}
      <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              Scan Docket
              <Badge variant="secondary" className="bg-purple-100 text-purple-700">AI-Powered</Badge>
            </DialogTitle>
            <DialogDescription>
              Upload or capture a photo of a paper docket. AI will extract the information automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Image Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                ocrImagePreview ? 'border-green-300 bg-green-50' : 'border-muted-foreground/25 hover:border-[#ffd108]'
              }`}
            >
              {ocrImagePreview ? (
                <div className="space-y-4">
                  <img 
                    src={ocrImagePreview} 
                    alt="Docket preview" 
                    className="max-h-64 mx-auto rounded-lg shadow-md"
                  />
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">{ocrImage?.name}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setOcrImage(null);
                      setOcrImagePreview(null);
                      setOcrResult(null);
                    }}
                  >
                    Choose Different Image
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-muted rounded-full">
                      <ImageIcon className="h-10 w-10 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium">Drop your docket image here</p>
                    <p className="text-sm text-muted-foreground">or click to browse</p>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Image
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        // For mobile devices, this will open camera
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.onchange = (e) => handleFileSelect(e);
                        input.click();
                      }}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {/* Extract Button */}
            {ocrImagePreview && !ocrResult && (
              <Button 
                className="w-full btn-volt" 
                onClick={handleOcrExtract}
                disabled={ocrLoading}
              >
                {ocrLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extracting data...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract Docket Data
                  </>
                )}
              </Button>
            )}

            {/* OCR Results */}
            {ocrResult && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                    <CheckCircle2 className="h-5 w-5" />
                    Extracted Information
                    {ocrResult.confidence && (
                      <Badge variant="outline" className={`ml-auto ${
                        ocrResult.confidence === 'high' ? 'border-green-500 text-green-700' :
                        ocrResult.confidence === 'medium' ? 'border-amber-500 text-amber-700' :
                        'border-red-500 text-red-700'
                      }`}>
                        {ocrResult.confidence} confidence
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="label-overline text-xs text-green-700">Date</p>
                      <p className="font-medium">{ocrResult.date || '-'}</p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Hours</p>
                      <p className="font-medium">{ocrResult.hours || '-'}</p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Customer</p>
                      <p className="font-medium">
                        {ocrResult.matched_customer_name || ocrResult.customer || '-'}
                        {ocrResult.matched_customer_id && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Site</p>
                      <p className="font-medium">
                        {ocrResult.matched_site_name || ocrResult.site || '-'}
                        {ocrResult.matched_site_id && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Asset Type</p>
                      <p className="font-medium">
                        {ocrResult.matched_equipment_type || ocrResult.equipment_type || '-'}
                        {ocrResult.matched_equipment_type && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Worker</p>
                      <p className="font-medium">{ocrResult.operator || '-'}</p>
                    </div>
                  </div>
                  {ocrResult.description && (
                    <div>
                      <p className="label-overline text-xs text-green-700">Description/Notes</p>
                      <p className="font-medium">{ocrResult.description}</p>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-green-200">
                    <p className="text-sm text-green-700">
                      <strong>Next step:</strong> Use this information to create a docket from a confirmed job in Jobs & Schedule.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOcrDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Docket Sheet (Flyout Panel) */}
      <Sheet open={editDialogOpen} onOpenChange={(open) => { if (open) { setEditDialogOpen(true); } else { requestCloseEdit(); } }}>
        <SheetContent side="right" className="w-full max-w-[1200px] sm:w-[1200px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2 flex-wrap">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Docket #{editingDocket?.docket_number}
              {(() => {
                const emails = docketEmailInfo?.emails || [];
                if (!emails.length) return null;
                const recipients = [...new Set(emails.map(e => (e.to_email || '').toLowerCase()).filter(Boolean))];
                if (!recipients.length) return null;
                return (
                  <button
                    type="button"
                    onClick={() => editingDocket && handleViewEmailStatus(editingDocket)}
                    data-testid="docket-emailed-badge"
                    title={`Emailed to: ${recipients.join(', ')}`}
                  >
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-300 dark:border-green-800 hover:bg-green-200 dark:hover:bg-green-900/60 cursor-pointer gap-1 font-normal">
                      <Mail className="h-3 w-3" />
                      Emailed to {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
                    </Badge>
                  </button>
                );
              })()}
            </SheetTitle>
          </SheetHeader>
          
          {editingDocket && (
            <>
            <Tabs defaultValue="docket" className="mt-4">
              <TabsList className={`grid w-full ${canViewFinancial ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <TabsTrigger value="docket" className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Docket
                </TabsTrigger>
                {canViewFinancial && (
                  <TabsTrigger value="invoice" className="flex items-center gap-2">
                    <FileDown className="h-4 w-4" />
                    Invoice
                  </TabsTrigger>
                )}
              </TabsList>
              
              {/* Docket Tab - Submitted Docket Data */}
              <TabsContent value="docket" className="space-y-6 pt-4">
                {editingDocket?.status !== 'draft' && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                    This docket is <strong>{editingDocket?.status?.replace('_', ' ')}</strong>. Fields are read-only. Use the footer's <em>Reset Docket</em> button to re-open it for editing.
                  </div>
                )}
                {editingDocket?.status === 'draft' && isEditing && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
                    <strong>Edit mode.</strong> Changes to hours, times, work completed, and notes will be saved when you click Save Changes. Invoice will regenerate automatically.
                  </div>
                )}
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(editingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {editingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {editingDocket.site_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    {getStatusBadge(editingDocket.status || 'draft')}
                    {editingDocket.grouped_invoice && (editingDocket.group_docket_ids?.length > 1) && (
                      <Badge
                        variant="outline"
                        className="mt-1 flex w-fit items-center gap-1 border-indigo-400 text-indigo-600 dark:text-indigo-300"
                        data-testid="docket-grouped-badge-drawer"
                      >
                        <Layers className="h-3 w-3" />
                        Grouped on 1 invoice ({editingDocket.group_docket_ids.length} dockets)
                      </Badge>
                    )}
                  </div>
                </div>

                {/* PO Number — flows to the invoice & accounting export (Customer Reference) */}
                {(() => {
                  const isExported = editingDocket.status === 'exported'
                    || editingDocket.xero_invoice_id || editingDocket.myob_invoice_id || editingDocket.qbo_invoice_id;
                  return (
                    <div className="rounded-lg border border-[#ffd108]/40 bg-[#ffd108]/5 p-4" data-testid="docket-po-section">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="label-overline text-xs flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" /> PO Number
                          </p>
                          <p className="text-xs text-muted-foreground">Exports to the accounting package as the Customer Reference.</p>
                        </div>
                        {isExported ? (
                          <span className="font-semibold text-lg" data-testid="docket-po-value">{editingDocket.po_number || '—'}</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editPoNumber}
                              onChange={(e) => setEditPoNumber(e.target.value)}
                              placeholder="Enter customer PO"
                              className="w-48 h-9"
                              data-testid="docket-po-input"
                            />
                            <Button
                              size="sm"
                              onClick={handleSavePoNumber}
                              disabled={savingPo || (editPoNumber.trim() === (editingDocket.po_number || ''))}
                              data-testid="docket-po-save-btn"
                            >
                              {savingPo ? 'Saving…' : 'Save PO'}
                            </Button>
                          </div>
                        )}
                      </div>
                      {isExported && (
                        <p className="text-[11px] text-muted-foreground mt-1">Locked — docket already exported.</p>
                      )}
                    </div>
                  );
                })()}

                {/* Invoice Reference — editable until exported */}
                {(() => {
                  const isExported = editingDocket.status === 'exported'
                    || editingDocket.xero_invoice_id || editingDocket.myob_invoice_id || editingDocket.qbo_invoice_id;
                  return (
                    <div className="rounded-lg border border-border p-4" data-testid="docket-ref-section">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="label-overline text-xs flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" /> Invoice Reference
                          </p>
                          <p className="text-xs text-muted-foreground">Preview and customise the reference sent to the accounting package.</p>
                        </div>
                        {isExported ? (
                          <span className="font-semibold text-lg" data-testid="docket-ref-value">{editingDocket.invoice_reference || '—'}</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editInvoiceRef}
                              onChange={(e) => setEditInvoiceRef(e.target.value)}
                              placeholder={editingDocket.docket_number || 'Enter reference'}
                              className="w-56 h-9"
                              data-testid="docket-ref-input"
                            />
                            <Button
                              size="sm"
                              onClick={handleSaveInvoiceRef}
                              disabled={savingRef || (editInvoiceRef.trim() === (editingDocket.invoice_reference || ''))}
                              data-testid="docket-ref-save-btn"
                            >
                              {savingRef ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        )}
                      </div>
                      {isExported && (
                        <p className="text-[11px] text-muted-foreground mt-1">Locked — docket already exported.</p>
                      )}
                    </div>
                  );
                })()}


                {/* Route Stops (read-only) — haulage/multi-stop */}
                {(editingDocket.job_stops?.length || editingDocket.depot_departed_at || editingDocket.depot_arrived_at) ? (
                  (() => {
                    const tfmt = (iso) => {
                      if (!iso) return null;
                      const d = new Date(iso);
                      if (isNaN(d.getTime())) return (iso.split('T')[1] || '').slice(0, 5) || null;
                      return formatTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`, tenantSettings.time_format);
                    };
                    const stops = (editingDocket.job_stops || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
                    return (
                      <div data-testid="docket-route-stops">
                        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-[#ffd108]" /> Route Stops
                          {editingDocket.route_summary ? <span className="text-xs font-normal text-muted-foreground">— {editingDocket.route_summary}</span> : null}
                        </h3>
                        {(editingDocket.depot_departed_at || editingDocket.depot_arrived_at) && (
                          <div className="flex items-center gap-6 text-sm mb-2 p-2 bg-muted/50 rounded flex-wrap" data-testid="docket-depot-times">
                            <span>Depot Depart: <b>{tfmt(editingDocket.depot_departed_at) || '—'}</b>{editingDocket.depot_departed_km != null ? ` (${editingDocket.depot_departed_km} km)` : ''}</span>
                            <span>Depot Arrival: <b>{tfmt(editingDocket.depot_arrived_at) || '—'}</b>{editingDocket.depot_arrived_km != null ? ` (${editingDocket.depot_arrived_km} km)` : ''}</span>
                            {editingDocket.depot_departed_km != null && editingDocket.depot_arrived_km != null && editingDocket.depot_arrived_km >= editingDocket.depot_departed_km && (
                              <span>Distance: <b>{editingDocket.depot_arrived_km - editingDocket.depot_departed_km} km</b></span>
                            )}
                          </div>
                        )}
                        {stops.length > 0 && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="label-overline">#</TableHead>
                                <TableHead className="label-overline">Type</TableHead>
                                <TableHead className="label-overline">Location</TableHead>
                                <TableHead className="label-overline">ETA</TableHead>
                                <TableHead className="label-overline">Arrived</TableHead>
                                <TableHead className="label-overline">Status</TableHead>
                                <TableHead className="label-overline">Works</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {stops.map((s, i) => (
                                <TableRow key={s.id || i} data-testid={`docket-stop-row-${i}`}>
                                  <TableCell>{i + 1}</TableCell>
                                  <TableCell className="capitalize">{s.type || 'stop'}</TableCell>
                                  <TableCell className="max-w-[220px] truncate">{s.label || [s.address, s.suburb, s.state].filter(Boolean).join(', ') || '-'}</TableCell>
                                  <TableCell>{s.est_arrival ? formatTime(s.est_arrival, tenantSettings.time_format) : '-'}</TableCell>
                                  <TableCell className="font-medium">{tfmt(s.arrived_at) || '-'}</TableCell>
                                  <TableCell className="capitalize">{s.status || '-'}</TableCell>
                                  <TableCell className="max-w-[240px] text-xs whitespace-pre-wrap">{s.work_description || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    );
                  })()
                ) : null}

                {/* Submitted Hours */}
                <div>
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ffd108]" />
                    Hours Recorded
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const docketItems = ((editingDocket?.status === 'draft' && editItems.length) ? editItems : (editingDocket.items || []))
                        .filter(item => item.item_type !== 'material' && item.item_type !== 'toll' && item.item_type !== 'fuel_levy' && !item.rule_generated && !item.invoice_added);
                      
                      // Build asset requirement grouping for multi-asset jobs
                      const assetReqs = editingDocket?.asset_requirements || [];
                      const isMultiAssetDocket = assetReqs.length > 0;
                      let lastAssetReqId = null;

                      // "Copy times" handler: copies start/finish/lunch from first asset equipment item to target
                      const copyTimesFromFirst = (targetItem) => {
                        const firstEquipItem = docketItems.find(i => i.item_type === 'equipment' && i.asset_requirement_id && i.asset_requirement_id !== targetItem.asset_requirement_id);
                        if (!firstEquipItem) return;
                        const patch = {
                          start_time: firstEquipItem.start_time,
                          finish_time: firstEquipItem.finish_time,
                          lunch_minutes: firstEquipItem.lunch_minutes,
                          travel_minutes: firstEquipItem.travel_minutes,
                          depart_depot_time: firstEquipItem.depart_depot_time,
                          arrive_depot_time: firstEquipItem.arrive_depot_time,
                          travel_out_minutes: firstEquipItem.travel_out_minutes,
                          travel_return_minutes: firstEquipItem.travel_return_minutes,
                        };
                        const recomp = computeHoursFromTimes(patch.start_time, patch.finish_time, patch.lunch_minutes, patch.travel_minutes);
                        if (recomp !== null) { patch.qty = recomp; patch.submitted_qty = recomp; patch.travel_excluded_hours = 0; }
                        setEditItems(prev => prev.map(it => it.id === targetItem.id ? { ...it, ...patch } : it));
                      };

                      return docketItems.map((item, idx) => {
                      const isDraft = editingDocket?.status === 'draft' && isEditing;
                      const _isHaulageOrMultiStop = !!(editingDocket?.job_stops?.length || editingDocket?.depot_departed_at || editingDocket?.depot_arrived_at);
                      const depotSettingOn = !!(isFlagEnabled('docket_depot_times') && tenantSettings.docket_depot_times_enabled);
                      const depotEffective = !_isHaulageOrMultiStop && (depotSettingOn || hasDepotTimes(item));
                      const isConcrete = item.item_type === 'concrete';
                      const unitLabel = isConcrete ? (item.uom || 'm³') : 'h';
                      const editIdx = isDraft ? editItems.findIndex(it => (it.id && it.id === item.id) || (!it.id && it === item)) : -1;
                      const updateEditItem = (patch) => {
                        setEditItems(prev => prev.map((it, i) => i === editIdx ? { ...it, ...patch } : it));
                      };
                      const handleTimeChange = (field, part, value) => {
                        const current = item[field] || '00:00';
                        const [h, m] = current.split(':');
                        const next = part === 'h' ? `${value}:${m || '00'}` : `${h || '00'}:${value}`;
                        const patch = { [field]: next };
                        // Build the item snapshot with the new value for derivation
                        const snapshot = { ...item, [field]: next };
                        // In depot mode, derive travel when any of the 4 times change
                        const depotFields = ['depart_depot_time', 'arrive_depot_time', 'start_time', 'finish_time'];
                        if (depotEffective && depotFields.includes(field)) {
                          const derived = deriveDepotTravel(snapshot);
                          if (derived) {
                            patch.travel_out_minutes = derived.travel_out_minutes;
                            patch.travel_return_minutes = derived.travel_return_minutes;
                            patch.travel_minutes = derived.travel_minutes;
                          } else {
                            patch.travel_out_minutes = null;
                            patch.travel_return_minutes = null;
                          }
                        }
                        const newStart = field === 'start_time' ? next : item.start_time;
                        const newFinish = field === 'finish_time' ? next : item.finish_time;
                        const travelForCalc = patch.travel_minutes ?? item.travel_minutes;
                        const recomputed = computeHoursFromTimes(newStart, newFinish, item.lunch_minutes, travelForCalc);
                        if (recomputed !== null) { patch.qty = recomputed; patch.submitted_qty = recomputed; patch.travel_excluded_hours = 0; }
                        updateEditItem(patch);
                      };

                      return (
                      <React.Fragment key={item.id || idx}>
                      {/* Multi-asset section header */}
                      {isMultiAssetDocket && item.asset_requirement_id && item.asset_requirement_id !== lastAssetReqId && (() => {
                        lastAssetReqId = item.asset_requirement_id;
                        const req = assetReqs.find(r => r.id === item.asset_requirement_id);
                        const isFirstAsset = idx === 0 || !docketItems.slice(0, idx).some(i => i.asset_requirement_id);
                        return (
                          <div className="flex items-center gap-2 py-1.5 mb-1" data-testid={`asset-section-${item.asset_requirement_id}`}>
                            <div className="h-px flex-1 bg-border" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              {req?.equipment_type || 'Asset'}
                            </span>
                            {!isFirstAsset && editingDocket?.status === 'draft' && isEditing && (
                              <button
                                type="button"
                                className="text-[10px] text-[#ffd108] hover:text-[#ffd108]/80 font-medium"
                                onClick={() => copyTimesFromFirst(item)}
                                data-testid={`copy-times-${item.asset_requirement_id}`}
                              >
                                Copy times
                              </button>
                            )}
                            <div className="h-px flex-1 bg-border" />
                          </div>
                        );
                      })()}
                      <div className={`p-3 rounded-lg ${item.rule_generated ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-muted/50'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {item.item_type === 'equipment' ? (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${item.rule_generated ? 'bg-amber-500/20' : 'bg-[#ffd108]/20'}`}>
                                <Truck className={`h-4 w-4 ${item.rule_generated ? 'text-amber-500' : 'text-[#ffd108]'}`} />
                              </div>
                            ) : item.item_type === 'worker' ? (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${item.rule_generated ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                                <User className={`h-4 w-4 ${item.rule_generated ? 'text-amber-500' : 'text-blue-500'}`} />
                              </div>
                            ) : isConcrete ? (
                              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                                <Package className="h-4 w-4 text-orange-500" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center shrink-0">
                                <Wrench className="h-4 w-4 text-gray-500" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium whitespace-pre-wrap">{item.display_name || item.type_name}</p>
                                {item.rule_generated && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-500">Auto</Badge>
                                )}
                                {item.included_in_asset && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-500/50 text-green-500" title={item.included_note || 'Included in asset rate'} data-testid={`included-badge-${item.id || idx}`}>Included</Badge>
                                )}
                              </div>
                              {item.rule_warning && (
                                <p className="text-[10px] text-amber-500">{item.rule_warning}</p>
                              )}
                              {isDraft && !item.rule_generated && (item.item_type === 'equipment' || item.item_type === 'worker') ? (
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                                  {/* Depart Depot (depot mode only) */}
                                  {depotEffective && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Depart Depot:</span>
                                    <Select value={(item.depart_depot_time || '').split(':')[0] || ''} onValueChange={(v) => handleTimeChange('depart_depot_time', 'h', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select value={(item.depart_depot_time || '').split(':')[1] || ''} onValueChange={(v) => handleTimeChange('depart_depot_time', 'm', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  )}
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{depotEffective ? 'Arrive Site:' : 'Start:'}</span>
                                    <Select value={(item.start_time || '').split(':')[0] || ''} onValueChange={(v) => handleTimeChange('start_time', 'h', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select value={(item.start_time || '').split(':')[1] || ''} onValueChange={(v) => handleTimeChange('start_time', 'm', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{depotEffective ? 'Depart Site:' : 'Finish:'}</span>
                                    <Select value={(item.finish_time || '').split(':')[0] || ''} onValueChange={(v) => handleTimeChange('finish_time', 'h', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select value={(item.finish_time || '').split(':')[1] || ''} onValueChange={(v) => handleTimeChange('finish_time', 'm', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  {/* Arrive Depot (depot mode only) */}
                                  {depotEffective && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Arrive Depot:</span>
                                    <Select value={(item.arrive_depot_time || '').split(':')[0] || ''} onValueChange={(v) => handleTimeChange('arrive_depot_time', 'h', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <span>:</span>
                                    <Select value={(item.arrive_depot_time || '').split(':')[1] || ''} onValueChange={(v) => handleTimeChange('arrive_depot_time', 'm', v)}>
                                      <SelectTrigger className="h-7 w-[64px] text-xs font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  )}
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Lunch (min):</span>
                                    <Input type="number" min="0" step="5" value={item.lunch_minutes ?? 0}
                                      onChange={(e) => {
                                        const lunch = parseInt(e.target.value || '0', 10);
                                        const recomputed = computeHoursFromTimes(item.start_time, item.finish_time, lunch, item.travel_minutes);
                                        updateEditItem({ lunch_minutes: lunch, ...(recomputed !== null ? { qty: recomputed, submitted_qty: recomputed, travel_excluded_hours: 0 } : {}) });
                                      }}
                                      className="h-7 w-16 text-xs font-mono" />
                                  </div>
                                  {depotEffective ? (
                                    <span className="text-muted-foreground text-xs font-mono">
                                      Travel: {item.travel_out_minutes != null && item.travel_return_minutes != null
                                        ? `+${item.travel_out_minutes} out / +${item.travel_return_minutes} return min`
                                        : '— (set depot times)'}
                                    </span>
                                  ) : (
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Travel (min):</span>
                                    <Input type="number" min="0" step="5" value={item.travel_minutes ?? 0}
                                      onChange={(e) => {
                                        const travel = parseInt(e.target.value || '0', 10);
                                        const recomputed = computeHoursFromTimes(item.start_time, item.finish_time, item.lunch_minutes, travel);
                                        updateEditItem({ travel_minutes: travel, ...(recomputed !== null ? { qty: recomputed, submitted_qty: recomputed, travel_excluded_hours: 0 } : {}) });
                                      }}
                                      className="h-7 w-16 text-xs font-mono" />
                                  </div>
                                  )}
                                  </div>
                              ) : (
                                <>
                                  {(item.start_time || item.finish_time) && (
                                    <div className="flex items-center gap-4 text-xs flex-wrap">
                                      {hasDepotTimes(item) && item.depart_depot_time && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-muted-foreground">Depart Depot:</span>
                                          <span className="font-mono text-foreground">{formatTime(item.depart_depot_time, tenantSettings.time_format)}</span>
                                        </span>
                                      )}
                                      {item.start_time && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-muted-foreground">{hasDepotTimes(item) ? 'Arrive Site:' : 'Start Time:'}</span>
                                          <span className="font-mono text-foreground">{formatTime(item.start_time, tenantSettings.time_format)}</span>
                                        </span>
                                      )}
                                      {item.finish_time && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-muted-foreground">{hasDepotTimes(item) ? 'Depart Site:' : 'Finish Time:'}</span>
                                          <span className="font-mono text-foreground">{formatTime(item.finish_time, tenantSettings.time_format)}</span>
                                        </span>
                                      )}
                                      {hasDepotTimes(item) && item.arrive_depot_time && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-muted-foreground">Arrive Depot:</span>
                                          <span className="font-mono text-foreground">{formatTime(item.arrive_depot_time, tenantSettings.time_format)}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                      {item.lunch_minutes > 0 && (<span className="flex items-center gap-1"><span className="text-amber-500">Lunch:</span> -{item.lunch_minutes}min</span>)}
                                      {hasDepotTimes(item) && item.travel_out_minutes != null && item.travel_return_minutes != null
                                        ? (<span className="flex items-center gap-1"><span className="text-blue-500">Travel:</span> +{item.travel_out_minutes} out / +{item.travel_return_minutes} return min</span>)
                                        : item.travel_minutes > 0 && (<span className="flex items-center gap-1"><span className="text-blue-500">Travel:</span> +{item.travel_minutes}min</span>)
                                      }
                                    </div>
                                  )}
                                  {/* SMU (meter) readings — equipment items, when captured on the docket */}
                                  {item.item_type === 'equipment' && (item.start_smu != null || item.finish_smu != null) && (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid={`docket-smu-${item.id || idx}`}>
                                      <span className="flex items-center gap-1">
                                        <span className="text-[#ffd108]">Meter Start:</span>
                                        <span className="font-mono text-foreground">{Number(item.start_smu ?? 0).toFixed(1)}</span>
                                        <span className="text-muted-foreground">{item.smu_unit || 'Hours'}</span>
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="text-[#ffd108]">Finish:</span>
                                        <span className="font-mono text-foreground">{Number(item.finish_smu ?? 0).toFixed(1)}</span>
                                      </span>
                                      {item.start_smu != null && item.finish_smu != null && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-emerald-500">Δ</span>
                                          <span className="font-mono text-foreground">
                                            {(Number(item.finish_smu) - Number(item.start_smu)).toFixed(1)}
                                          </span>
                                          <span className="text-muted-foreground">{item.smu_unit || 'Hours'}</span>
                                        </span>
                                      )}
                                      {(item.start_smu_2 != null || item.finish_smu_2 != null) && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-blue-400">2nd:</span>
                                          <span className="font-mono text-foreground">
                                            {Number(item.start_smu_2 ?? 0).toFixed(1)} → {Number(item.finish_smu_2 ?? 0).toFixed(1)}
                                          </span>
                                          <span className="text-muted-foreground">{item.smu_unit_2 || 'KM'}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                              {/* Total qty (editable in draft/edit-mode, read-only otherwise) */}
                              {isDraft && !item.rule_generated ? (
                                <div className="flex items-center gap-2 pt-1">
                                  <span className="text-xs text-muted-foreground">{isConcrete ? 'Concrete Pumped:' : 'Total Hours:'}</span>
                                  <Input type="number" step={isConcrete ? '0.5' : '0.25'} min="0" value={item.submitted_qty ?? item.qty ?? 0}
                                    onChange={(e) => { const v = parseFloat(e.target.value || '0'); updateEditItem({ qty: v, submitted_qty: v }); }}
                                    className="h-8 w-24 text-right font-mono font-bold text-amber-600 dark:text-[#ffd108]"
                                    data-testid={`docket-item-qty-${idx}`} />
                                  <span className="text-xs text-muted-foreground">{unitLabel}</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          {/* Right-side qty (only shown when NOT in edit mode — otherwise it's under the fields above) */}
                          {!(isDraft && !item.rule_generated) && (
                            <div className="text-right shrink-0">
                              <p className="font-mono font-bold text-amber-600 dark:text-[#ffd108]">{item.submitted_qty ?? item.qty}{isConcrete ? ` ${unitLabel}` : 'h'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      </React.Fragment>
                      );
                    });
                    })()}
                  </div>
                </div>

                {/* Route Progress (haulage jobs) */}
                <RouteProgressList docket={editingDocket} tenantSettings={tenantSettings} variant="cards" api={api} onStopsChanged={(stops) => setEditingDocket(prev => prev ? { ...prev, job_stops: stops } : prev)} />

                {/* Material Loads Section — hidden for crane_hire tenants (no material deliveries) */}
                {!isCraneHire && editingDocket.material_loads && editingDocket.material_loads.length > 0 && (
                  <div data-testid="docket-material-loads">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Mountain className="h-4 w-4 text-orange-500" />
                      Material Loads
                    </h3>
                    <div className="space-y-2">
                      {(editingDocket?.status === 'draft' && editMaterialLoads.length > 0 ? editMaterialLoads : editingDocket.material_loads).map((load, idx) => (
                        <div key={load.id || idx} className={`p-3 rounded-lg ${load.on_customer_account ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                                <Package className="h-4 w-4 text-orange-500" />
                              </div>
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {load.material_name || load.material_type_name || 'Unknown Material'}
                                  {load.on_customer_account && (
                                    <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500" data-testid={`load-customer-account-badge-${idx}`}>
                                      Customer Account
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">{load.yard_name || 'Unknown Location'}</p>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              {editingDocket?.status === 'draft' && isEditing && (
                                <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`load-customer-account-toggle-${idx}`}>
                                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">Customer Acct</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!load.on_customer_account}
                                    onClick={() => {
                                      setEditMaterialLoads(prev => prev.map((l, i) =>
                                        i === idx ? { ...l, on_customer_account: !l.on_customer_account } : l
                                      ));
                                    }}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                      load.on_customer_account ? 'bg-amber-500' : 'bg-muted-foreground/30'
                                    }`}
                                  >
                                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                      load.on_customer_account ? 'translate-x-4' : 'translate-x-0'
                                    }`} />
                                  </button>
                                </label>
                              )}
                              <div>
                                <p className={`font-mono font-semibold ${load.on_customer_account ? 'text-amber-500 line-through' : 'text-orange-500'}`}>
                                  {load.quantity} {load.unit}
                                </p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  load.load_type === 'pickup' ? 'bg-blue-500/20 text-blue-500' :
                                  load.load_type === 'disposal' ? 'bg-amber-500/20 text-amber-500' :
                                  'bg-green-500/20 text-green-500'
                                }`}>
                                  {load.load_type === 'pickup' ? 'Pickup' : 
                                   load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {(load.tip_docket_number || load.notes) && (
                            <div className="mt-2 ml-11 text-xs text-muted-foreground">
                              {load.tip_docket_number && (
                                <span className="mr-3">Docket #: {load.tip_docket_number}</span>
                              )}
                              {load.notes && (
                                <span>{load.notes}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Support Assets Section — non-billable logistics equipment */}
                {editingDocket.support_assets && editingDocket.support_assets.length > 0 && (
                  <div data-testid="docket-support-assets">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-cyan-500" />
                      Support Assets
                      <span className="text-[10px] font-normal text-cyan-500 bg-cyan-500/10 px-1.5 py-0.5 rounded">Non-Billable</span>
                    </h3>
                    <div className="space-y-2">
                      {editingDocket.support_assets.map((sa, idx) => (
                        <div key={sa.id || idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-dashed border-cyan-500/20">
                          <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <Truck className="h-4 w-4 text-cyan-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{sa.equipment_name || sa.asset_id || 'Support Asset'}</p>
                            <p className="text-xs text-muted-foreground">
                              {sa.equipment_type}{sa.rego ? ` \u2022 ${sa.rego}` : ''}
                              {' \u2022 '}{sa.start_date}{sa.end_date && sa.end_date !== sa.start_date ? ` \u2192 ${sa.end_date}` : ''}
                            </p>
                            {sa.notes && <p className="text-xs text-muted-foreground mt-0.5">{sa.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tolls Section */}
                {(() => {
                  const tollItems = (editingDocket?.status === 'draft' ? editItems : editingDocket.items || [])
                    .filter(item => item.item_type === 'toll');
                  if (tollItems.length === 0) return null;
                  return (
                    <div data-testid="docket-tolls">
                      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-purple-400" />
                        Tolls
                      </h3>
                      <div className="space-y-2">
                        {tollItems.map((item, idx) => (
                          <div key={item.id || idx} className="bg-muted/50 p-3 rounded-lg flex items-center justify-between" data-testid={`docket-toll-${idx}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <Receipt className="h-4 w-4 text-purple-400" />
                              </div>
                              <p className="font-medium">{item.type_name || item.display_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-semibold text-purple-400">Qty: {item.qty}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}


                {/* Trailer Load Deliveries (P3) */}
                {(isEditing ? editLoadDeliveries : editingDocket.load_deliveries)?.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-amber-500" />
                      Trailer Load ({(isEditing ? editLoadDeliveries : editingDocket.load_deliveries).length})
                    </h3>
                    <div className="space-y-2" data-testid="docket-load-deliveries">
                      {(isEditing ? editLoadDeliveries : editingDocket.load_deliveries).map((d, idx) => {
                        const shortId = (d.item_id || '').slice(0, 8) || 'Unknown';
                        const label = d.item_type === 'equipment'
                          ? (equipment.find(x => x.id === d.item_id)?.asset_id || equipment.find(x => x.id === d.item_id)?.name || shortId)
                          : (attachments.find(x => x.id === d.item_id)?.name || attachments.find(x => x.id === d.item_id)?.asset_id || shortId);
                        const statusStyle = {
                          delivered: 'bg-green-500/20 text-green-500',
                          damaged: 'bg-red-500/20 text-red-500',
                          not_delivered: 'bg-amber-500/20 text-amber-500',
                          pending: 'bg-muted text-muted-foreground',
                        }[d.status] || 'bg-muted text-muted-foreground';
                        return (
                          <div key={`load-${idx}`} className="bg-muted/50 p-3 rounded-lg flex items-center gap-3" data-testid={`docket-load-row-${idx}`}>
                            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                              {d.item_type === 'attachment' ? <Wrench className="h-4 w-4 text-amber-500" /> : <Truck className="h-4 w-4 text-amber-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{label}</p>
                              <p className="text-xs text-muted-foreground capitalize">{d.item_type}{d.confirmed_at ? ` · confirmed ${formatDateTime(d.confirmed_at, tenantSettings.date_format, tenantSettings.time_format)}` : ''}</p>
                            </div>
                            {isEditing ? (
                              <Select
                                value={d.status}
                                onValueChange={(val) =>
                                  setEditLoadDeliveries(prev =>
                                    prev.map((r, i) => i === idx ? { ...r, status: val } : r)
                                  )
                                }
                              >
                                <SelectTrigger className="w-36 h-8" data-testid={`docket-load-status-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="delivered">Delivered</SelectItem>
                                  <SelectItem value="not_delivered">Not delivered</SelectItem>
                                  <SelectItem value="damaged">Damaged</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyle}`}>
                                {d.status?.replace('_', ' ') || 'pending'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {isEditing && editLoadDeliveries.some(d => d.status === 'pending') && editLoadDeliveries.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setEditLoadDeliveries(prev => prev.map(r => r.status === 'pending' ? { ...r, status: 'delivered' } : r))
                          }
                          data-testid="docket-load-mark-all-delivered"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Mark all pending as delivered
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Work Completed */}
                {editingDocket?.status === 'draft' && isEditing ? (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Work Completed</h3>
                    <Textarea
                      value={editWorkCompleted}
                      onChange={(e) => setEditWorkCompleted(e.target.value)}
                      placeholder="Describe the work completed on site..."
                      rows={3}
                      data-testid="docket-work-completed"
                    />
                  </div>
                ) : editingDocket.work_completed && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Work Completed</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{editingDocket.work_completed}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {editingDocket?.status === 'draft' && isEditing ? (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Docket Notes</h3>
                    <Textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Additional notes (optional)..."
                      rows={2}
                      data-testid="docket-notes-edit"
                    />
                  </div>
                ) : editingDocket.notes && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Docket Notes</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{editingDocket.notes}</p>
                    </div>
                  </div>
                )}

                {/* Post-Submission Notes */}
                <div className="border-t pt-4" data-testid="post-submission-notes-section">
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <StickyNote className="h-4 w-4 text-amber-500" />
                    Post-Submission Notes
                    {(editingDocket.post_submission_notes || []).length > 0 && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                        {(editingDocket.post_submission_notes || []).length}
                      </Badge>
                    )}
                  </h3>
                  {(editingDocket.post_submission_notes || []).length > 0 && (
                    <div className="space-y-2 mb-3">
                      {(editingDocket.post_submission_notes || []).map((note) => (
                        <div
                          key={note.id}
                          className="p-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/40 rounded-lg"
                          data-testid={`post-note-${note.id}`}
                        >
                          <p className="text-sm">{note.text}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {note.author_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {note.created_at ? formatDateTime(note.created_at, tenantSettings.date_format, tenantSettings.time_format) : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add note form */}
                  <div className="flex gap-2">
                    <Textarea
                      value={postNoteText}
                      onChange={(e) => setPostNoteText(e.target.value)}
                      placeholder="Add a post-submission note..."
                      rows={2}
                      className="flex-1"
                      data-testid="post-note-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleAddPostNote}
                      disabled={!postNoteText.trim() || postNoteSubmitting}
                      className="self-end"
                      data-testid="post-note-submit-btn"
                    >
                      {postNoteSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                </div>

                {/* Edit Reason (draft + edit mode only) */}
                {editingDocket?.status === 'draft' && isEditing && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">
                      Edit Reason <span className="text-muted-foreground font-normal text-sm">(optional — recorded in audit trail)</span>
                    </h3>
                    <Textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="e.g. Corrected hours after operator clarification..."
                      rows={2}
                      data-testid="docket-edit-reason"
                    />
                  </div>
                )}

                {/* Customer Sign-off */}
                {editingDocket.customer_signoff_confirmed && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Customer Sign-off
                    </h3>
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <p className="font-medium">{editingDocket.customer_signoff_name || 'Customer'}</p>
                        <p className="text-sm text-muted-foreground">
                          {editingDocket.customer_signoff_signature ? 'Confirmed & Signed' : 'Confirmed (signature not captured)'}
                        </p>
                        {editingDocket.signature_signed_via === 'remote' && editingDocket.signature_signed_at && (
                          <p className="text-sm text-muted-foreground">
                            Remotely signed: {formatDateTime(editingDocket.signature_signed_at, tenantSettings.date_format, tenantSettings.time_format)}
                          </p>
                        )}
                      </div>
                      {editingDocket.customer_signoff_signature && (
                        <div className="ml-auto bg-white rounded p-2">
                          <img 
                            src={editingDocket.customer_signoff_signature} 
                            alt="Customer Signature" 
                            className="h-12 max-w-[150px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(editingDocket.signature_skipped || (editingDocket.customer_signoff_confirmed && !editingDocket.customer_signoff_signature)) && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Signature Not Captured
                      {editingDocket.signature_request_status === 'requested' && (
                        <Badge variant="outline" className="ml-2 border-amber-500 text-amber-600 text-xs" data-testid="sig-requested-badge">
                          Signature requested{editingDocket.signature_request_sent_to ? ` · ${editingDocket.signature_request_sent_to}` : ''}
                        </Badge>
                      )}
                    </h3>
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-1">
                      {editingDocket.signature_skip_reason && (
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          {editingDocket.signature_skip_reason === 'no_one_on_site' ? 'No one on site' : editingDocket.signature_skip_reason === 'customer_refused' ? 'Customer refused' : 'Other'}
                        </p>
                      )}
                      {!editingDocket.signature_skip_reason && !editingDocket.signature_skipped && (
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          Operator confirmed completion but did not capture a signature
                        </p>
                      )}
                      {editingDocket.signature_skip_notes && (
                        <p className="text-sm text-muted-foreground">{editingDocket.signature_skip_notes}</p>
                      )}
                      {canApprove && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 border-amber-500 text-amber-600 hover:bg-amber-500/10"
                          onClick={() => handleOpenSignatureDialog(editingDocket)}
                          data-testid="drawer-request-signature-btn"
                        >
                          <PenLine className="h-3.5 w-3.5 mr-1.5" />
                          {editingDocket.signature_request_status === 'requested' ? 'Resend Signature Request' : 'Request Signature'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Docket Photos */}
                {editingDocket.photos && editingDocket.photos.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Camera className="h-4 w-4 text-blue-500" />
                      Photos ({editingDocket.photos.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {editingDocket.photos.map((photo, idx) => {
                        const blobUrl = photoBlobUrls[idx];
                        const capturedAt = typeof photo === 'object' ? photo?.captured_at : null;
                        const hasTs = typeof photo === 'object' ? photo?.has_timestamp_overlay : false;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => blobUrl && setLightboxUrl(blobUrl)}
                            className="relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                            data-testid={`docket-photo-view-${idx}`}
                          >
                            {blobUrl ? (
                              <>
                                <img src={blobUrl} alt={`Docket photo ${idx + 1}`} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity font-medium">View</span>
                                </div>
                                {hasTs && (
                                  <div className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded">Timestamped</div>
                                )}
                                {capturedAt && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-2 py-1 text-center">
                                    {formatDateTime(capturedAt, tenantSettings.date_format, tenantSettings.time_format)}
                                  </div>
                                )}
                              </>
                            ) : blobUrl === null ? (
                              <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">Unavailable</div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-muted animate-pulse" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Submitted Timestamp */}
                {editingDocket.created_at && (
                  <div className="text-sm text-muted-foreground text-right pt-2 border-t space-y-0.5">
                    <div>Submitted: {formatDateTime(editingDocket.created_at, tenantSettings.date_format, tenantSettings.time_format)}</div>
                    {editingDocket.signature_signed_via === 'remote' && editingDocket.signature_signed_at && (
                      <div>Customer signed: {formatDateTime(editingDocket.signature_signed_at, tenantSettings.date_format, tenantSettings.time_format)}</div>
                    )}
                  </div>
                )}

              {/* Audit Trail — Enhanced with diffs and restore */}
              {editingDocket?.edit_history && editingDocket.edit_history.length > 0 && (
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="font-heading font-semibold flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    Edit History ({editingDocket.edit_history.length})
                  </h3>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="docket-edit-history">
                    {[...editingDocket.edit_history].reverse().map((entry, idx) => {
                      const isRestore = entry.action === 'version_restored';
                      const isReset = entry.action === 'reset_to_draft';
                      const hasSnapshot = Array.isArray(entry.items_before);
                      const itemsBefore = entry.items_before || [];
                      const notesBefore = entry.notes_before;
                      const workBefore = entry.work_completed_before;
                      const loadsBefore = entry.material_loads_before || [];

                      // Determine the "after" state: the items_before of the NEXT edit
                      // (since entries are stored chronologically, the entry AFTER this one
                      //  in the original array holds what the docket looked like after this edit).
                      const originalIdx = editingDocket.edit_history.length - 1 - idx;
                      const nextEntry = editingDocket.edit_history[originalIdx + 1];
                      const itemsAfter = nextEntry ? (nextEntry.items_before || []) : (editingDocket.items || []);
                      const notesAfter = nextEntry ? nextEntry.notes_before : editingDocket.notes;
                      const workAfter = nextEntry ? nextEntry.work_completed_before : editingDocket.work_completed;

                      const itemCountChanged = hasSnapshot && itemsBefore.length !== itemsAfter.length;
                      const notesChanged = hasSnapshot && notesBefore !== notesAfter;
                      const workChanged = hasSnapshot && workBefore !== workAfter;
                      const hasChanges = itemCountChanged || notesChanged || workChanged;
                      const canRestore = hasSnapshot && editingDocket.status === 'draft';

                      return (
                        <details key={entry.id || idx} className={`rounded-lg border text-sm ${
                          isRestore ? 'bg-blue-500/5 border-blue-500/20' : isReset ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted/40 border-border'
                        }`} data-testid={`edit-history-entry-${idx}`}>
                          <summary className="cursor-pointer list-none p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-medium truncate">
                                  {entry.edited_by_name || entry.edited_by_email || 'Unknown'}
                                </span>
                                {isReset && (
                                  <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600 shrink-0">
                                    Reset from {entry.previous_status?.replace('_', ' ') || 'prior status'}
                                  </Badge>
                                )}
                                {isRestore && (
                                  <Badge variant="outline" className="text-[10px] border-blue-500/50 text-blue-600 shrink-0">
                                    Version restored
                                  </Badge>
                                )}
                                {!isReset && !isRestore && hasChanges && (
                                  <Badge variant="outline" className="text-[10px] shrink-0">
                                    {itemCountChanged ? `${itemsBefore.length} → ${itemsAfter.length} items` : 'Edited'}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                                {entry.edited_at ? formatDateTime(entry.edited_at, tenantSettings.date_format, tenantSettings.time_format) : ''}
                              </span>
                            </div>
                            {entry.reason && (
                              <p className="mt-1 text-muted-foreground italic text-xs">"{entry.reason}"</p>
                            )}
                          </summary>

                          <div className="px-3 pb-3 space-y-2 border-t border-border/50 pt-2">
                            {entry.voided_invoices && entry.voided_invoices.length > 0 && (
                              <p className="text-xs text-amber-600">
                                Voided: {entry.voided_invoices.map(v => `${v.platform} ${v.invoice_number || v.invoice_id}`).join(', ')}
                              </p>
                            )}

                            {hasSnapshot && (
                              <>
                                {/* Items diff */}
                                <div className="space-y-1">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Line Items</p>
                                  {itemsBefore.length === 0 && itemsAfter.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No line items in either version</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {/* Show "before" items */}
                                      {itemsBefore.map((it, i) => {
                                        const afterMatch = itemsAfter.find(a => a.id === it.id);
                                        const wasRemoved = !afterMatch;
                                        const qtyChanged = afterMatch && Number(afterMatch.qty) !== Number(it.qty);
                                        const timeChanged = afterMatch && (afterMatch.start_time !== it.start_time || afterMatch.finish_time !== it.finish_time);
                                        return (
                                          <div key={it.id || i} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${wasRemoved ? 'bg-red-500/10 text-red-600' : (qtyChanged || timeChanged) ? 'bg-amber-500/10 text-amber-700' : 'text-muted-foreground'}`}>
                                            {wasRemoved ? <X className="h-3 w-3 shrink-0" /> : (qtyChanged || timeChanged) ? <Pencil className="h-3 w-3 shrink-0" /> : <Check className="h-3 w-3 shrink-0" />}
                                            <span className="truncate">{it.display_name || it.type_name || 'Item'}</span>
                                            {it.start_time && it.finish_time && <span className="shrink-0">{it.start_time}–{it.finish_time}</span>}
                                            <span className="shrink-0">{it.qty} {it.uom}</span>
                                            {wasRemoved && <span className="font-semibold">(removed)</span>}
                                            {qtyChanged && <span className="font-semibold">→ {afterMatch.qty} {afterMatch.uom}</span>}
                                            {timeChanged && !qtyChanged && <span className="font-semibold">→ {afterMatch.start_time}–{afterMatch.finish_time}</span>}
                                          </div>
                                        );
                                      })}
                                      {/* Items added after this version */}
                                      {itemsAfter.filter(a => !itemsBefore.some(b => b.id === a.id)).map((it, i) => (
                                        <div key={`added-${it.id || i}`} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-green-500/10 text-green-700">
                                          <Plus className="h-3 w-3 shrink-0" />
                                          <span className="truncate">{it.display_name || it.type_name || 'Item'}</span>
                                          <span className="shrink-0">{it.qty} {it.uom}</span>
                                          <span className="font-semibold">(added)</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Notes diff */}
                                {notesChanged && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</p>
                                    <div className="text-xs grid grid-cols-2 gap-2">
                                      <div className="px-2 py-1 rounded bg-red-500/10">
                                        <span className="text-muted-foreground">Before: </span>
                                        <span>{notesBefore || '(empty)'}</span>
                                      </div>
                                      <div className="px-2 py-1 rounded bg-green-500/10">
                                        <span className="text-muted-foreground">After: </span>
                                        <span>{notesAfter || '(empty)'}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Work completed diff */}
                                {workChanged && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Work Completed</p>
                                    <div className="text-xs grid grid-cols-2 gap-2">
                                      <div className="px-2 py-1 rounded bg-red-500/10">
                                        <span className="text-muted-foreground">Before: </span>
                                        <span>{workBefore || '(empty)'}</span>
                                      </div>
                                      <div className="px-2 py-1 rounded bg-green-500/10">
                                        <span className="text-muted-foreground">After: </span>
                                        <span>{workAfter || '(empty)'}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {!hasChanges && !isReset && !isRestore && (
                                  <p className="text-xs text-muted-foreground italic">No visible changes to items or notes.</p>
                                )}
                              </>
                            )}

                            {!hasSnapshot && !isReset && (
                              <p className="text-xs text-muted-foreground italic">No snapshot data available for this entry.</p>
                            )}

                            {/* Restore button */}
                            {canRestore && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full mt-1 text-xs border-blue-500/30 text-blue-600 hover:bg-blue-500/10"
                                onClick={(e) => { e.preventDefault(); setRestoreTarget(entry); }}
                                data-testid={`restore-version-btn-${idx}`}
                              >
                                <RotateCcw className="h-3 w-3 mr-1.5" />
                                Restore to this version
                              </Button>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </div>
              )}

              </TabsContent>
              
              {/* Invoice Tab - Financial access only */}
              {canViewFinancial && (
              <TabsContent value="invoice" className="space-y-6 pt-4">
                {editingDocket?.status === 'draft' ? (
                  isEditing ? (
                    <p className="text-sm text-muted-foreground">
                      Edit line items and pricing for invoicing. Changes cannot be made after approval.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Invoice preview. Click <strong>Edit</strong> in the footer to modify line items.
                    </p>
                  )
                ) : (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This docket is <strong>{editingDocket?.status?.replace('_', ' ')}</strong>. Invoice details are read-only.
                    </p>
                  </div>
                )}
              {/* Header Info - Read Only */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(editingDocket.job_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {editingDocket.customer_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {editingDocket.site_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Status</p>
                  {getStatusBadge(editingDocket.status || 'draft')}
                </div>
              </div>

              {/* Price List Indicator */}
              {usedPriceList && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm">
                    Prices calculated using: <span className="font-semibold text-green-700 dark:text-green-300">{usedPriceList}</span>
                  </span>
                </div>
              )}

              {/* Editable Items Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold">Docket Items</h3>
                  <div className="flex items-center gap-2">
                    {editingDocket?.status === 'draft' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRecalculatePrices}
                          disabled={recalculating}
                          className="flex items-center gap-2"
                        >
                          {recalculating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                          Recalculate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddLineItem}
                          className="flex items-center gap-2"
                          data-testid="add-line-item-btn"
                        >
                          <Plus className="h-4 w-4" />
                          Add Line
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenCatalog}
                          className="flex items-center gap-2"
                          data-testid="add-from-catalog-btn"
                        >
                          <ListPlus className="h-4 w-4" />
                          Add from Catalog
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              data-testid="add-toll-btn"
                            >
                              <Plus className="h-4 w-4" />
                              Add Toll
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                            {tolls.filter(t => t.is_active).length === 0 ? (
                              <DropdownMenuItem disabled>No tolls configured</DropdownMenuItem>
                            ) : (
                              tolls.filter(t => t.is_active).map(toll => (
                                <DropdownMenuItem
                                  key={toll.id}
                                  onClick={() => handleAddToll(toll)}
                                  data-testid={`add-toll-option-${toll.id}`}
                                >
                                  <span className="flex-1">{toll.name}</span>
                                  <span className="text-muted-foreground ml-3 font-mono text-xs">
                                    {tenantSettings.currency_symbol}{(toll.price ?? 0).toFixed(2)}
                                  </span>
                                </DropdownMenuItem>
                              ))
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow>
                        {showItemCode && <TableHead className="label-overline w-[90px] text-xs px-2">Item Code</TableHead>}
                        {showAccountId && <TableHead className="label-overline w-[90px] text-xs px-2">Account ID</TableHead>}
                        <TableHead className="label-overline text-xs px-2">Description</TableHead>
                        <TableHead className="label-overline w-[80px] text-xs px-2">Qty</TableHead>
                        <TableHead className="label-overline w-[80px] text-xs px-2">UOM</TableHead>
                        <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Price</TableHead>
                        <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Total</TableHead>
                        {editingDocket?.status === 'draft' && (
                          <TableHead className="label-overline w-[40px] text-xs px-1"></TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const invAssetReqs = editingDocket?.asset_requirements || [];
                        const isMultiAssetInv = invAssetReqs.length > 0;
                        let lastInvReqId = null;

                        return editItems.filter(item => !item.included_in_asset).map((item) => {
                          const sectionHeader = isMultiAssetInv && item.asset_requirement_id && item.asset_requirement_id !== lastInvReqId ? (() => {
                            lastInvReqId = item.asset_requirement_id;
                            const req = invAssetReqs.find(r => r.id === item.asset_requirement_id);
                            return (
                              <TableRow key={`section-${item.asset_requirement_id}`} className="bg-muted/30">
                                <TableCell colSpan={5 + (showItemCode ? 1 : 0) + (showAccountId ? 1 : 0) + (editingDocket?.status === 'draft' ? 1 : 0)} className="py-1.5 px-3">
                                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <Truck className="h-3 w-3" />
                                    {req?.equipment_type || 'Asset'}
                                    {req?.quantity > 1 && <span className="text-[10px] font-normal">x{req.quantity}</span>}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })() : null;

                          return (
                            <React.Fragment key={item.id}>
                              {sectionHeader}
                              <TableRow className={`h-10 ${item.rule_generated ? 'bg-amber-500/5' : ''}`}>
                          {showItemCode && <TableCell className="px-2 py-1">
                            <Input
                              value={item.item_code || ''}
                              onChange={(e) => handleEditItemChange(item.id, 'item_code', e.target.value)}
                              placeholder="Code"
                              className="font-mono text-xs h-7 px-2"
                              disabled={!(editingDocket?.status === 'draft' && isEditing)}
                            />
                          </TableCell>}
                          {showAccountId && <TableCell className="px-2 py-1">
                            <Input
                              value={item.account_item_id || ''}
                              onChange={(e) => handleEditItemChange(item.id, 'account_item_id', e.target.value)}
                              placeholder="ID"
                              className="font-mono text-xs h-7 px-2"
                              disabled={!(editingDocket?.status === 'draft' && isEditing)}
                            />
                          </TableCell>}
                          <TableCell className="px-2 py-1">
                            <div className="flex items-center gap-2">
                              {item.item_type && (
                                <Badge 
                                  variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                                  className={`text-[10px] px-1.5 py-0 shrink-0 ${item.item_type === 'worker' ? 'bg-blue-600 text-white' : ''} ${item.item_type === 'material' ? 'bg-orange-500/20 text-orange-400' : ''} ${item.item_type === 'toll' ? 'bg-purple-500/20 text-purple-400' : ''} ${item.item_type === 'fuel_levy' ? 'bg-green-500/20 text-green-400' : ''}`}
                                >
                                  {item.item_type === 'equipment' ? 'E' : item.item_type === 'worker' ? 'L' : item.item_type === 'material' ? 'M' : item.item_type === 'toll' ? 'T' : item.item_type === 'fuel_levy' ? 'F' : 'A'}
                                </Badge>
                              )}
                              {item.rule_generated && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-500/50 text-amber-500 shrink-0">Auto</Badge>
                              )}
                              {editingDocket?.status === 'draft' && isEditing ? (
                                <Textarea
                                  value={item.description || item.display_name || item.type_name || ''}
                                  onChange={(e) => handleEditItemChange(item.id, 'description', e.target.value)}
                                  placeholder="Line item description..."
                                  className="text-xs min-h-[28px] px-2 py-1 flex-1 resize-none"
                                  rows={1}
                                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                />
                              ) : (
                                <span className="text-xs whitespace-pre-wrap break-words flex-1 py-1">
                                  {item.description || item.display_name || item.type_name || ''}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.qty}
                              onChange={(e) => handleEditItemChange(item.id, 'qty', parseFloat(e.target.value) || 0)}
                              className="font-mono text-xs h-7 px-2 w-full text-center"
                              disabled={!(editingDocket?.status === 'draft' && isEditing)}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Select
                              value={item.uom}
                              onValueChange={(v) => handleEditItemChange(item.id, 'uom', v)}
                              disabled={!(editingDocket?.status === 'draft' && isEditing)}
                            >
                              <SelectTrigger className="h-7 text-xs px-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="HOUR">HOUR</SelectItem>
                                <SelectItem value="DAY">DAY</SelectItem>
                                <SelectItem value="WEEK">WEEK</SelectItem>
                                <SelectItem value="EACH">EACH</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={item.unit_price || 0}
                              onChange={(e) => handleEditItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="font-mono text-xs h-7 px-2 text-right w-full"
                              disabled={!(editingDocket?.status === 'draft' && isEditing)}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right font-mono text-xs font-medium">
                            {tenantSettings.currency_symbol}{((parseFloat(item.billed_qty ?? item.qty) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                          </TableCell>
                          {editingDocket?.status === 'draft' && (
                            <TableCell className="px-1 py-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLineItem(item.id)}
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                data-testid={`remove-line-${item.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                              </React.Fragment>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals Section */}
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="w-64 space-y-2">
                    {(() => {
                      const subtotal = editItems.reduce((sum, item) => sum + ((parseFloat(item.billed_qty ?? item.qty) || 0) * (parseFloat(item.unit_price) || 0)), 0);
                      const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                      const taxAmount = subtotal * (taxRate / 100);
                      const total = subtotal + taxAmount;
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                          </div>
                          {taxRate > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t">
                            <span className="font-semibold">Total</span>
                            <span className="text-xl font-bold font-mono text-amber-600 dark:text-[#ffd108]">
                              {tenantSettings.currency_symbol}{total.toFixed(2)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Notes + Edit Reason live on the Docket tab when editing */}

              {/* Accounting Export History */}
              {(loadingExportHistory || exportHistory.length > 0) && (
                <div className="space-y-2 pt-4 border-t" data-testid="docket-export-history">
                  <h3 className="font-heading font-semibold flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    Export History {exportHistory.length > 0 && `(${exportHistory.length})`}
                  </h3>
                  {loadingExportHistory && (
                    <div className="flex justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {exportHistory.map((log) => (
                      <details
                        key={log.id}
                        className={`rounded-lg p-3 text-sm border ${
                          log.status === 'success'
                            ? 'bg-green-500/5 border-green-500/20'
                            : 'bg-red-500/5 border-red-500/20'
                        }`}
                      >
                        <summary className="cursor-pointer list-none flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Badge variant="outline" className="uppercase text-[10px] shrink-0">{log.platform}</Badge>
                            {log.status === 'success' ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                            )}
                            <span className="truncate">
                              {log.status === 'success'
                                ? `Invoice ${log.invoice_number || log.invoice_id || ''}`
                                : (log.error_summary || 'Export failed')}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {log.attempted_at ? formatDateTime(log.attempted_at, tenantSettings.date_format, tenantSettings.time_format) : ''}
                          </span>
                        </summary>
                        {log.status !== 'success' && (
                          <div className="mt-2 pt-2 border-t border-red-200/40 space-y-1 text-xs">
                            {log.error_detail && <p>{log.error_detail}</p>}
                            {log.suggested_fix && (
                              <p className="border-l-2 border-red-400 pl-2">
                                <span className="font-semibold">Fix:</span> {log.suggested_fix}
                              </p>
                            )}
                            {log.attempted_by_name && (
                              <p className="text-muted-foreground">Attempted by {log.attempted_by_name} · HTTP {log.http_status || '—'}</p>
                            )}
                            {log.technical_response && (
                              <pre className="p-2 rounded bg-red-100 dark:bg-red-950/60 text-[11px] whitespace-pre-wrap break-all max-h-32 overflow-auto">
                                {log.technical_response}
                              </pre>
                            )}
                          </div>
                        )}
                      </details>
                    ))}
                  </div>
                </div>
              )}

              </TabsContent>
              )}
            </Tabs>

            <SheetFooter className="pt-4 border-t mt-6 flex-wrap gap-2">
              <div className="flex gap-2 flex-1 flex-wrap">
                <Button variant="outline" onClick={() => handleDownloadPDF(editingDocket)} data-testid="docket-download-pdf-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                {editingDocket?.status === 'draft' && (
                  <>
                    {!isEditing ? (
                      <>
                        <Button
                          onClick={() => setIsEditing(true)}
                          className="btn-volt"
                          data-testid="edit-docket-btn"
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        {tenantSettings.skip_docket_approval && canApprove ? (
                          <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => { setEditDialogOpen(false); handleConfirmDocket(editingDocket.id); }} data-testid="edit-confirm-docket-btn">
                            <Check className="h-4 w-4 mr-2" />
                            Confirm Docket
                          </Button>
                        ) : (
                          <Button variant="outline" onClick={() => { setEditDialogOpen(false); handleSubmitForApproval(editingDocket.id); }}>
                            <Send className="h-4 w-4 mr-2" />
                            Submit for Approval
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => {
                            // Discard local edits — reseed from source
                            setEditItems(editingDocket.items?.map(it => ({ ...it })) || []);
                            setEditNotes(editingDocket.notes || '');
                            setEditWorkCompleted(editingDocket.work_completed || '');
                            setEditMaterialLoads(editingDocket.material_loads?.map(l => ({ ...l })) || []);
                            setEditReason('');
                            setIsEditing(false);
                          }}
                          data-testid="cancel-edit-docket-btn"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button onClick={handleSaveEdit} className="btn-volt" disabled={saving} data-testid="save-docket-edit-btn">
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                        {tenantSettings.skip_docket_approval && canApprove ? (
                          <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" disabled={saving} onClick={() => handleSaveThen(() => handleConfirmDocket(editingDocket.id))} data-testid="edit-save-confirm-docket-btn">
                            <Check className="h-4 w-4 mr-2" />
                            Save &amp; Confirm
                          </Button>
                        ) : (
                          <Button variant="outline" disabled={saving} onClick={() => handleSaveThen(() => handleSubmitForApproval(editingDocket.id))} data-testid="edit-save-submit-docket-btn">
                            <Send className="h-4 w-4 mr-2" />
                            Save &amp; Submit
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
                {editingDocket?.status === 'pending_approval' && canApprove && (
                  <>
                    <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => { setEditDialogOpen(false); handleApproveDocket(editingDocket.id); }} data-testid="edit-approve-docket-btn">
                      <Check className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setEditDialogOpen(false); handleRejectDocket(editingDocket.id); }}>
                      <X className="h-4 w-4 mr-2" />
                      Return to Draft
                    </Button>
                  </>
                )}
                {(editingDocket?.status === 'approved' || editingDocket?.status === 'exported') && (
                  <>
                    <Button variant="outline" onClick={() => handleGeneratePDF(editingDocket)}>
                      <FileDown className="h-4 w-4 mr-2" />
                      PDF Invoice
                    </Button>
                    {tenantSettings.xero_connected && (
                      <Button variant="outline" onClick={() => { setEditDialogOpen(false); handleExportToXero(editingDocket); }}>
                        <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                        Export to Xero
                      </Button>
                    )}
                    {tenantSettings.myob_connected && (
                      <Button variant="outline" onClick={() => { setEditDialogOpen(false); handleExportToMyob(editingDocket); }}>
                        <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                        Export to MYOB
                      </Button>
                    )}
                    {tenantSettings.qbo_connected && (
                      <Button variant="outline" onClick={() => { setEditDialogOpen(false); handleExportToQbo(editingDocket); }}>
                        <ExternalLink className="h-4 w-4 mr-2 text-green-600" />
                        Export to QuickBooks
                      </Button>
                    )}
                    {canApprove && (
                      <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setEditDialogOpen(false); handleResetDocket(editingDocket.id); }} data-testid="reset-docket-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset Docket
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Button variant="outline" onClick={requestCloseEdit} data-testid="close-docket-edit-btn">
                Close
              </Button>
            </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Unsaved-changes guard for the docket editor */}
      <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <AlertDialogContent data-testid="discard-edits-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You've made changes to this docket's line items that haven't been saved. If you close now, those changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="keep-editing-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-discard-btn"
              onClick={() => { setDiscardConfirmOpen(false); setEditDialogOpen(false); }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore version confirmation dialog */}
      <AlertDialog open={!!restoreTarget} onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}>
        <AlertDialogContent data-testid="restore-version-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to previous version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the current line items, notes, and work completed with the data from{' '}
              <strong>
                {restoreTarget?.edited_at
                  ? formatDateTime(restoreTarget.edited_at, tenantSettings.date_format, tenantSettings.time_format)
                  : 'the selected version'}
              </strong>.
              {restoreTarget?.items_before && (
                <span className="block mt-1">
                  The restored version has <strong>{restoreTarget.items_before.length}</strong> line item{restoreTarget.items_before.length !== 1 ? 's' : ''}.
                </span>
              )}
              <span className="block mt-2 text-foreground font-medium">The current state will be saved in the audit trail so this action can be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} data-testid="cancel-restore-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              disabled={restoring}
              onClick={handleRestoreVersion}
              data-testid="confirm-restore-btn"
            >
              {restoring ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Restoring...</> : <><RotateCcw className="h-4 w-4 mr-1.5" /> Restore version</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* XERO Export Dialog */}
      <Dialog open={xeroDialogOpen} onOpenChange={setXeroDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Push to Xero
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {tenantSettings.xero_connected ? (
              <>
                {/* Connected - Show push confirmation */}
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="font-medium text-green-800 dark:text-green-200">Xero Connected</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Ready to push invoice</p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <p className="label-overline text-xs">Invoice Details</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Customer</span>
                        <span className="font-medium text-sm">{selectedDocket.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Date</span>
                        <span className="font-medium text-sm">{formatDate(selectedDocket.job_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Items</span>
                        <span className="font-medium text-sm">{selectedDocket.items?.length || 0} line items</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-amber-600 dark:text-[#ffd108]">{tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    This will create a <strong>Draft</strong> invoice in Xero. You can review and send it from within Xero.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Not Connected */}
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#13B5EA]/10 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">Connect Xero First</h3>
                  <p className="text-muted-foreground text-sm">
                    Connect your Xero account to push approved dockets as invoices.
                  </p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <p className="label-overline text-xs">Selected Docket</p>
                    <p className="font-medium">{selectedDocket.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(selectedDocket.job_date)} • {tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Connection Required</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Go to Settings → Accounting to connect your Xero account.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setXeroDialogOpen(false)}>
              Cancel
            </Button>
            {tenantSettings.xero_connected ? (
              <Button 
                onClick={handlePushToXero}
                disabled={xeroPushing}
                className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
              >
                {xeroPushing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Push to Xero
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/settings?tab=accounting'}
                className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MYOB Export Dialog */}
      <Dialog open={myobDialogOpen} onOpenChange={setMyobDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                <path d="M8 12h8M8 8h8M8 16h5" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Push to MYOB
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {tenantSettings.myob_connected ? (
              <>
                {/* Connected - Show push confirmation */}
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="font-medium text-purple-800 dark:text-purple-200">MYOB Connected</p>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Ready to push invoice</p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <p className="label-overline text-xs">Invoice Details</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Customer</span>
                        <span className="font-medium text-sm">{selectedDocket.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Date</span>
                        <span className="font-medium text-sm">{formatDate(selectedDocket.job_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Items</span>
                        <span className="font-medium text-sm">{selectedDocket.items?.length || 0} line items</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-amber-600 dark:text-[#ffd108]">{tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    This will create an <strong>Open</strong> invoice in MYOB. You can review and send it from within MYOB.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Not Connected */}
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                      <path d="M8 12h8M8 8h8M8 16h5" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">Connect MYOB First</h3>
                  <p className="text-muted-foreground text-sm">
                    Connect your MYOB account to push approved dockets as invoices.
                  </p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <p className="label-overline text-xs">Selected Docket</p>
                    <p className="font-medium">{selectedDocket.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(selectedDocket.job_date)} • {tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Connection Required</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Go to Settings → Accounting to connect your MYOB account.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMyobDialogOpen(false)}>
              Cancel
            </Button>
            {tenantSettings.myob_connected ? (
              <Button 
                onClick={handlePushToMyob}
                disabled={myobPushing}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {myobPushing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Push to MYOB
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/settings?tab=accounting'}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QuickBooks Push Dialog */}
      <Dialog open={qboDialogOpen} onOpenChange={setQboDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Send className="h-5 w-5 text-green-500" />
              Push to QuickBooks
            </DialogTitle>
            <DialogDescription>
              Create a draft invoice in QuickBooks Online
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {tenantSettings.qbo_connected ? (
              <>
                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <p className="label-overline text-xs">Invoice Details</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Customer</span>
                        <span className="font-medium text-sm">{selectedDocket.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Date</span>
                        <span className="font-medium text-sm">{formatDate(selectedDocket.job_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Items</span>
                        <span className="font-medium text-sm">{selectedDocket.items?.length || 0} line items</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-amber-600 dark:text-[#ffd108]">{tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    This will create a <strong>Draft</strong> invoice in QuickBooks. You can review and send it from within QuickBooks.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <h3 className="font-heading font-semibold text-lg mb-2">Connect QuickBooks First</h3>
                  <p className="text-muted-foreground text-sm">
                    Connect your QuickBooks Online account to push approved dockets as invoices.
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Connection Required</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Go to Settings → Accounting to connect your QuickBooks Online account.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQboDialogOpen(false)}>
              Cancel
            </Button>
            {tenantSettings.qbo_connected ? (
              <Button 
                onClick={handlePushToQbo}
                disabled={qboPushing}
                className="bg-green-600 hover:bg-green-700"
                data-testid="qbo-push-invoice-btn"
              >
                {qboPushing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Push to QuickBooks
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/settings?tab=accounting'}
                className="bg-green-600 hover:bg-green-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Docket to Customer Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#ffd108]" />
              Email Docket
            </DialogTitle>
            <DialogDescription>
              Select who should receive the PDF docket
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {emailDocket && (
              <div className="border rounded-lg p-3 space-y-1.5 bg-muted/30">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Docket #</span>
                  <span className="font-medium text-sm">{emailDocket.docket_number || emailDocket.id?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Customer</span>
                  <span className="font-medium text-sm">{emailDocket.customer_name}</span>
                </div>
              </div>
            )}

            {emailLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading contacts...</span>
              </div>
            ) : (
              <div className="space-y-3" data-testid="email-recipient-list">
                {/* Suggested recipients as checkboxes */}
                {emailRecipients.length > 0 ? (
                  <div className="space-y-1">
                    {emailRecipients.map((r) => (
                      <div
                        key={r.email}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${r.checked ? 'border-[#ffd108]/60 bg-[#ffd108]/5' : 'border-border'}`}
                      >
                        <Checkbox
                          checked={r.checked}
                          onCheckedChange={() => toggleEmailRecipient(r.email)}
                          data-testid={`email-recipient-${r.email}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{r.source}</span>
                        {r.source === 'Custom' && (
                          <button onClick={() => removeEmailRecipient(r.email)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-email-${r.email}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">No contacts found — add an email below</p>
                )}

                {/* Add custom email */}
                <div className="flex gap-2" data-testid="add-custom-email-row">
                  <Input
                    type="email"
                    placeholder="Add email address..."
                    value={emailCustomInput}
                    onChange={(e) => setEmailCustomInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomEmailRecipient(); } }}
                    className="text-sm h-9"
                    data-testid="custom-email-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-9"
                    onClick={addCustomEmailRecipient}
                    disabled={!emailCustomInput.trim()}
                    data-testid="add-custom-email-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                The email will include a <strong>PDF attachment</strong> of the complete docket with all job details, work items, and material loads.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEmailDialogOpen(false);
              setEmailDocket(null);
              setEmailRecipients([]);
              setEmailCustomInput('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendDocketEmail}
              disabled={emailSending || getSelectedEmails().length === 0}
              className="btn-volt"
              data-testid="send-docket-email-btn"
            >
              {emailSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send{getSelectedEmails().length > 1 ? ` to ${getSelectedEmails().length}` : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Signature Dialog */}
      <Dialog open={sigDialogOpen} onOpenChange={setSigDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <PenLine className="h-5 w-5 text-amber-500" />
              Request Signature
            </DialogTitle>
            <DialogDescription>
              Email the customer a secure link to sign this docket remotely.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {sigDocket && (
              <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Docket #</span>
                  <span className="font-medium text-sm">{sigDocket.docket_number || sigDocket.id?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Customer</span>
                  <span className="font-medium text-sm">{sigDocket.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Date</span>
                  <span className="font-medium text-sm">{formatDate(sigDocket.job_date)}</span>
                </div>
              </div>
            )}

            {sigLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading contacts...</span>
              </div>
            ) : (
              <div className="space-y-3" data-testid="sig-recipient-options">
                {sigAddress && (
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sigMode === 'customer' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'border-border hover:bg-muted/50'}`}
                    data-testid="sig-option-customer"
                  >
                    <input type="radio" name="sigMode" checked={sigMode === 'customer'} onChange={() => setSigMode('customer')} className="mt-1 accent-[#ffd108]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Customer email</p>
                      <p className="text-xs text-muted-foreground truncate">{sigAddress}</p>
                    </div>
                  </label>
                )}

                {(sigContacts.customer_contacts.length > 0 || sigContacts.site_contacts.length > 0) && (
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sigMode === 'contact' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'border-border hover:bg-muted/50'}`}
                    data-testid="sig-option-contact"
                  >
                    <input
                      type="radio"
                      name="sigMode"
                      checked={sigMode === 'contact'}
                      onChange={() => {
                        setSigMode('contact');
                        if (!sigSelectedContact) {
                          const all = [...sigContacts.customer_contacts, ...sigContacts.site_contacts];
                          if (all.length) setSigSelectedContact(all[0].email);
                        }
                      }}
                      className="mt-1 accent-[#ffd108]"
                    />
                    <div className="flex-1 min-w-0 space-y-2">
                      <p className="text-sm font-medium">Contact</p>
                      {sigMode === 'contact' && (
                        <select
                          value={sigSelectedContact}
                          onChange={(e) => setSigSelectedContact(e.target.value)}
                          className="w-full text-sm border rounded-md px-2 py-1.5 bg-background"
                          data-testid="sig-contact-select"
                        >
                          {sigContacts.customer_contacts.length > 0 && (
                            <optgroup label="Customer Contacts">
                              {sigContacts.customer_contacts.map(c => (
                                <option key={c.id} value={c.email}>{c.name}{c.position ? ` (${c.position})` : ''} — {c.email}</option>
                              ))}
                            </optgroup>
                          )}
                          {sigContacts.site_contacts.length > 0 && (
                            <optgroup label="Site Contacts">
                              {sigContacts.site_contacts.map(c => (
                                <option key={c.id} value={c.email}>{c.name}{c.role ? ` (${c.role})` : ''} — {c.email}</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      )}
                    </div>
                  </label>
                )}

                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sigMode === 'custom' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'border-border hover:bg-muted/50'}`}
                  data-testid="sig-option-custom"
                >
                  <input type="radio" name="sigMode" checked={sigMode === 'custom'} onChange={() => setSigMode('custom')} className="mt-1 accent-[#ffd108]" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-sm font-medium">Custom email</p>
                    {sigMode === 'custom' && (
                      <Input
                        type="email"
                        placeholder="recipient@example.com"
                        value={sigCustomEmail}
                        onChange={(e) => setSigCustomEmail(e.target.value)}
                        data-testid="sig-custom-email-input"
                        autoFocus
                      />
                    )}
                  </div>
                </label>
              </div>
            )}

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                The customer will receive a secure link to review and sign this docket. Once signed, a copy is emailed back automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSigDialogOpen(false); setSigDocket(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestSignature}
              disabled={sigSending || !getResolvedSigEmail()}
              className="btn-volt"
              data-testid="send-signature-request-btn"
            >
              {sigSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><PenLine className="h-4 w-4 mr-2" /> Send Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Email Status Dialog */}
      <Dialog open={emailStatusDialogOpen} onOpenChange={setEmailStatusDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <History className="h-5 w-5 text-[#ffd108]" />
              Email Delivery Status
            </DialogTitle>
            <DialogDescription>
              Track the delivery status of emails sent for this docket
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {emailStatusLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-muted-foreground">Loading email status...</span>
              </div>
            ) : emailStatus ? (
              <>
                {/* Summary Card */}
                <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground text-sm">Docket</span>
                    <span className="font-semibold">#{emailStatus.docket_number || emailDocket?.docket_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground text-sm">Emails Sent</span>
                    <span className="font-medium">{emailStatus.email_count || 0}</span>
                  </div>
                  {emailStatus.has_been_emailed && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Latest Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmailStatusColor(emailStatus.latest_status)}`}>
                          {emailStatus.latest_status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Last Sent To</span>
                        <span className="font-medium text-sm">{emailStatus.last_sent_to}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Last Sent At</span>
                        <span className="text-sm">{formatDate(emailStatus.last_sent_at)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Email History */}
                {emailStatus.emails && emailStatus.emails.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email History
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {emailStatus.emails.map((email, index) => (
                        <div key={email.id || index} className="border rounded-lg p-3 space-y-2 bg-card">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{email.to_email}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEmailStatusColor(email.status)}`}>
                              {email.status?.toUpperCase() || 'SENT'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(email.sent_at)}
                            </span>
                            {email.status_updated_at && email.status !== 'sent' && (
                              <span className="flex items-center gap-1">
                                <RefreshCw className="h-3 w-3" />
                                Updated: {formatDate(email.status_updated_at)}
                              </span>
                            )}
                          </div>
                          {email.bounce_reason && (
                            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                              <strong>Bounce reason:</strong> {email.bounce_reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <MailX className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No emails have been sent for this docket yet.</p>
                    <p className="text-xs mt-1">Use "Email to Customer" to send the docket.</p>
                  </div>
                )}

                {/* Status Legend */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2">STATUS LEGEND</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-yellow-500`}></span>
                      <span>Sent - Accepted for delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-green-500`}></span>
                      <span>Delivered - Reached inbox</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-blue-500`}></span>
                      <span>Opened - Recipient opened</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-red-500`}></span>
                      <span>Bounced - Delivery failed</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Unable to load email status.</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {emailStatus?.has_been_emailed && (
              <Button 
                variant="outline" 
                onClick={handleResendDocketEmail}
                disabled={emailSending}
                className="w-full sm:w-auto"
              >
                {emailSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Resend to Last Recipient
                  </>
                )}
              </Button>
            )}
            <Button 
              onClick={() => {
                setEmailStatusDialogOpen(false);
                setEmailStatus(null);
                setEmailDocket(null);
              }}
              className={emailStatus?.has_been_emailed ? "w-full sm:w-auto" : ""}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invoice To Customer Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />
              {invoicePreview?.already_sent ? 'Re-send Invoice To Customer' : 'Send Invoice To Customer'}
            </DialogTitle>
            <DialogDescription>
              {invoicePreview?.invoice_number
                ? <>Invoice <span className="font-medium">{invoicePreview.invoice_number}</span>{invoicePreview.accounting_system ? ` (${invoicePreview.accounting_system.toUpperCase()})` : ''} will be fetched and attached automatically.</>
                : 'The invoice PDF will be fetched from your accounting system and attached.'}
            </DialogDescription>
          </DialogHeader>

          {invoiceLoading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
              Loading invoice details…
            </div>
          ) : (
            <div className="space-y-4">
              {invoicePreview?.already_sent && invoicePreview?.invoice_sent_at && (
                <div className="text-xs bg-teal-50 text-teal-700 rounded-md px-3 py-2">
                  Already sent {new Date(invoicePreview.invoice_sent_at).toLocaleString()} to {(invoicePreview.invoice_sent_to || []).join(', ')}
                </div>
              )}

              <div>
                <label className="text-sm font-medium mb-1 block">Recipients</label>
                <div className="space-y-2">
                  {invoiceRecipients.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        type="email"
                        placeholder="customer@example.com"
                        value={r}
                        onChange={(e) => updateInvoiceRecipient(idx, e.target.value)}
                        data-testid={`invoice-recipient-${idx}`}
                      />
                      {invoiceRecipients.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeInvoiceRecipient(idx)} data-testid={`remove-recipient-${idx}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={addInvoiceRecipient} data-testid="add-recipient-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add email
                  </Button>
                  {(invoicePreview?.customer_contacts || []).concat(invoicePreview?.site_contacts || []).map((c) => (
                    <Button key={c.id} variant="ghost" size="sm" className="text-xs" onClick={() => setInvoiceRecipients(prev => prev.includes(c.email) ? prev : [...prev.filter(Boolean), c.email])}>
                      + {c.name || c.email}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Subject</label>
                <Input value={invoiceSubject} onChange={(e) => setInvoiceSubject(e.target.value)} data-testid="invoice-subject" />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Message</label>
                <Textarea rows={7} value={invoiceBody} onChange={(e) => setInvoiceBody(e.target.value)} data-testid="invoice-body" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)} disabled={invoiceSending}>Cancel</Button>
            <Button onClick={handleSendInvoice} disabled={invoiceSending || invoiceLoading} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-send-invoice">
              {invoiceSending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>) : (<><Send className="h-4 w-4 mr-2" />Send Invoice</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Docket"
        description={
          <div className="space-y-2">
            <p>Delete docket "{docketToDelete?.docket_number || ''}"? The job status will be reverted to Confirmed.</p>
            {isDocketExported(docketToDelete) && (
              <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-800 dark:text-amber-300" data-testid="delete-docket-exported-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>This docket has already been <strong>exported to accounting</strong>. Deleting it here will <strong>not</strong> remove the invoice from Xero/MYOB/QuickBooks and may leave your payroll/invoice records out of sync.</span>
              </div>
            )}
          </div>
        }
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-docket-dialog"
      />
      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedDockets.length} Docket${selectedDockets.length !== 1 ? 's' : ''}`}
        description={(() => {
          const exportedCount = dockets.filter(d => selectedDockets.includes(d.id) && isDocketExported(d)).length;
          return (
            <div className="space-y-2">
              <p>Permanently delete {selectedDockets.length} selected docket{selectedDockets.length !== 1 ? 's' : ''}? Associated jobs will be reverted to Confirmed. This cannot be undone.</p>
              {exportedCount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-800 dark:text-amber-300" data-testid="bulk-delete-exported-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span><strong>{exportedCount}</strong> of these {exportedCount === 1 ? 'has' : 'have'} already been <strong>exported to accounting</strong>. Deleting {exportedCount === 1 ? 'it' : 'them'} here will <strong>not</strong> remove the invoice{exportedCount === 1 ? '' : 's'} from Xero/MYOB/QuickBooks and may leave your payroll/invoice records out of sync.</span>
                </div>
              )}
            </div>
          );
        })()}
        confirmText={`Delete ${selectedDockets.length}`}
        variant="destructive"
        onConfirm={handleBulkDelete}
        isLoading={isBulkDeleting}
        testId="bulk-delete-docket-dialog"
      />
      {/* EW2-300: Group & Export selected dockets to one invoice */}
      <Dialog open={groupExportOpen} onOpenChange={setGroupExportOpen}>
        <DialogContent data-testid="group-export-dialog" className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Group &amp; Export to Invoice
            </DialogTitle>
            <DialogDescription>
              Combine the selected approved dockets into a single accounting invoice. Each docket appears as its own section with its items listed underneath.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const info = getGroupExportInfo();
            const customerName = info.objs[0]?.customer_name || '—';
            return (
              <div className="space-y-4">
                <div className="rounded-lg border p-3 text-sm space-y-1" data-testid="group-export-summary">
                  <div className="flex justify-between"><span className="text-muted-foreground">Dockets</span><span className="font-medium">{info.objs.length}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{info.sameCustomer ? customerName : 'Multiple'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Combined total</span><span className="font-medium">{tenantSettings.currency_symbol}{Number(info.total).toFixed(2)}</span></div>
                </div>

                {!info.sameCustomer && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-800 dark:text-amber-300 text-sm" data-testid="group-export-customer-warning">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>All grouped dockets must belong to the <strong>same customer</strong>. Please adjust your selection.</span>
                  </div>
                )}
                {info.sameCustomer && !info.allApproved && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-800 dark:text-amber-300 text-sm" data-testid="group-export-status-warning">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Only <strong>approved</strong> dockets that have <strong>not</strong> already been exported can be grouped.</span>
                  </div>
                )}

                {info.eligible && (
                  <div className="space-y-2">
                    <Label>Invoice preview</Label>
                    <div className="rounded-lg border max-h-64 overflow-y-auto divide-y" data-testid="group-export-preview">
                      {buildGroupPreview(info.objs).sections.map((sec) => (
                        <div key={sec.id} className="p-2" data-testid={`group-preview-section-${sec.id}`}>
                          <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-300">{sec.header}</div>
                          {sec.items.length === 0 ? (
                            <div className="pl-4 text-xs text-muted-foreground italic">No items</div>
                          ) : (
                            sec.items.map((it, i) => (
                              <div key={i} className="pl-4 flex justify-between gap-2 text-xs py-0.5">
                                <span className="text-muted-foreground truncate">{it.desc} <span className="tabular-nums">× {it.qty}</span></span>
                                <span className="font-mono shrink-0">{tenantSettings.currency_symbol}{it.amount.toFixed(2)}</span>
                              </div>
                            ))
                          )}
                        </div>
                      ))}
                      {(() => {
                        const p = buildGroupPreview(info.objs);
                        return (
                          <div className="p-2 bg-muted/50 text-xs space-y-0.5" data-testid="group-preview-totals">
                            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{tenantSettings.currency_symbol}{p.subtotal.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">{tenantSettings.tax_name} ({tenantSettings.tax_rate}%)</span><span className="font-mono">{tenantSettings.currency_symbol}{p.tax.toFixed(2)}</span></div>
                            <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono">{tenantSettings.currency_symbol}{p.total.toFixed(2)}</span></div>
                          </div>
                        );
                      })()}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Each docket becomes a section header with its items listed underneath. Tax is applied by the accounting package.</p>
                  </div>
                )}

                {info.eligible && info.platforms.length === 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-muted p-2 text-sm text-muted-foreground" data-testid="group-export-no-platform">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>No accounting package is connected. Connect Xero, MYOB, or QuickBooks in Settings → Accounting.</span>
                  </div>
                )}

                {info.eligible && info.platforms.length > 0 && (
                  <div className="space-y-2">
                    <Label>Export to</Label>
                    <div className="flex flex-col gap-2">
                      {info.platforms.map(p => (
                        <Button
                          key={p.key}
                          variant="outline"
                          className="justify-between"
                          disabled={groupExporting}
                          onClick={() => handleGroupExport(p)}
                          data-testid={`group-export-${p.key}-btn`}
                        >
                          <span className="flex items-center gap-2"><ExternalLink className="h-4 w-4" /> {p.label}</span>
                          {groupExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="text-xs text-muted-foreground">Create draft invoice</span>}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGroupExportOpen(false)} disabled={groupExporting} data-testid="group-export-cancel-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-accounting: choose which accounting system to export to */}
      <Dialog open={connChoiceOpen} onOpenChange={(o) => { if (!connChoiceBusy) { setConnChoiceOpen(o); if (!o) setPendingPush(null); } }}>
        <DialogContent className="max-w-md" data-testid="choose-accounting-system-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Choose {pendingPush?.platformLabel} company
            </DialogTitle>
            <DialogDescription>
              This customer isn't linked to a {pendingPush?.platformLabel} company yet. Pick which one to export to — we'll remember it for next time.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs label-overline">Accounting system</Label>
            <Select value={connChoiceSelected} onValueChange={setConnChoiceSelected}>
              <SelectTrigger className="mt-2" data-testid="choose-accounting-system-select">
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {connChoiceConnections.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`choose-accounting-option-${c.id}`}>
                    {c.label || c.company_name || c.provider}{c.is_default ? ' (default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConnChoiceOpen(false); setPendingPush(null); }} disabled={connChoiceBusy} data-testid="choose-accounting-cancel-btn">
              Cancel
            </Button>
            <Button onClick={confirmConnChoice} disabled={connChoiceBusy || !connChoiceSelected} data-testid="choose-accounting-confirm-btn">
              {connChoiceBusy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exporting...</>) : (<><Send className="h-4 w-4 mr-2" />Link &amp; Export</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Catalog Picker Dialog */}
      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Customer Price List Catalog</DialogTitle>
            <DialogDescription>Select items to add to this docket. Items will be added with qty 0 for you to fill in.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {catalogItems.map((item, idx) => {
              const alreadyAdded = editItems.some(
                e => e.item_code && e.item_code === item.item_code
              );
              return (
                <div
                  key={item.id || idx}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                    alreadyAdded ? 'bg-muted/40 border-muted' : 'hover:bg-muted/30 border-border cursor-pointer'
                  }`}
                  onClick={() => !alreadyAdded && handleAddCatalogItem(item)}
                  data-testid={`catalog-item-${item.item_code || idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {item.item_code && (
                        <Badge variant="outline" className="font-mono text-[10px] shrink-0">{item.item_code}</Badge>
                      )}
                      <span className="text-sm font-medium truncate">
                        {item.description || item.equipment_type || 'Item'}
                      </span>
                      {item.auto_apply && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-500/15 text-blue-600 shrink-0">Auto</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                      <span>${(item.rate || item.price || 0).toFixed(2)}/{item.uom || 'HOUR'}</span>
                      {item.account_item_id && <span className="font-mono">Acct: {item.account_item_id}</span>}
                    </div>
                  </div>
                  {alreadyAdded ? (
                    <Badge className="bg-green-500/20 text-green-600 text-xs shrink-0">Added</Badge>
                  ) : (
                    <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </div>
              );
            })}
            {catalogItems.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No catalog items found</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

export default Dockets;
